import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import api from '../api';
import { Team, Game, Player, GameVideo, Message, TeamStatsData, LeagueData, Tournament, TournamentScheduleData, TournamentBracketData, TeamSeason } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { mediaUrl } from '../utils/mediaUrl';
import { parseGameTime } from '../utils/gameTime';
import { TeamThemeProvider } from '../components/theme/TeamThemeProvider';
import { SeasonSponsorCard, useTeamSponsorPlacements } from '../components/SponsorComponents';

const API_URL   = process.env.REACT_APP_API_URL?.replace('/api', '') ?? 'http://localhost:3001';
const HLS_BASE  = process.env.REACT_APP_HLS_URL ?? 'http://localhost:8000';

const fmtDate = (s: string) =>
  parseGameTime(s).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

const fmtShortDate = (s: string) =>
  parseGameTime(s).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

// A season's display name: the free-form label if the admin set one ('Fall 2026'),
// otherwise the year.
const seasonName = (s: { season_label?: string | null; season_year: number }) =>
  s.season_label || String(s.season_year);

// ── Team-name cleaning utilities ──────────────────────────────────────────────
// Calendar imports often produce entries like:
//   "Turf Classic Match #2: Carmel FC 2015 Girls Gold"
//   "Carmel FC 2015 Girls Gold  [REMINDER]"
// This strips the event-title prefix before a colon and bracket suffixes.
const EVENT_PREFIX_RE = /^.{0,80}(?:match|game|classic|tournament|bracket|round|pool|turf|cup|showcase|championship|friendly)\s*[#\d]*\s*:\s*/i;
const BRACKET_SUFFIX_RE = /\s*\[.*?\]\s*$/gi;
const WHITESPACE_RE = /\s{2,}/g;

function cleanTeamName(raw: string | null | undefined): string {
  if (!raw) return 'TBD';
  let name = raw
    .replace(BRACKET_SUFFIX_RE, '')   // strip [REMINDER], [HOLD], etc.
    .replace(EVENT_PREFIX_RE, '')      // strip "Turf Classic #2: " prefix
    .replace(WHITESPACE_RE, ' ')       // collapse double spaces
    .trim();
  return name || 'TBD';
}

// Returns true if the cleaned name looks like a placeholder rather than a real team
const PLACEHOLDER_RE = /^(tbd|tba|bye|none|unknown|bracket\s+[a-z]\s*#?\d*|pool\s+[a-z]\s+team\s*\d*|team\s+\d+|#?\d+\s+seed)$/i;
function isPlaceholder(name: string): boolean {
  return PLACEHOLDER_RE.test(name.trim());
}

function displayTeamName(raw: string | null | undefined): string {
  const cleaned = cleanTeamName(raw);
  return isPlaceholder(cleaned) ? 'TBD' : cleaned;
}

// ── Video Player Modal ────────────────────────────────────────────────────────
const GameVideoModal = ({ game, onClose }: { game: Game & { recording?: GameVideo }; onClose: () => void }) => {
  const recording = game.recording;
  const videoUrl = recording
    ? mediaUrl(
        recording.file_path.startsWith('http') || recording.file_path.startsWith('/api/')
          ? recording.file_path
          : recording.file_path.startsWith('/uploads/')
            ? `${API_URL}${recording.file_path}`
            : `${HLS_BASE}${recording.file_path}`
      )
    : null;

  const hasScore = game.home_final_score != null && game.away_final_score != null;

  return (
    <div className="modal-overlay" onClick={onClose} style={{ alignItems: 'center', padding: '16px' }}>
      <div style={{ width: '100%', maxWidth: '960px' }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
          <div>
            <div style={{ fontWeight: 800, fontSize: '16px', color: 'var(--ink-1)' }}>{game.title}</div>
            <div style={{ fontSize: '13px', color: 'var(--ink-3)' }}>{fmtDate(game.scheduled_at)}</div>
          </div>
          <button className="modal-close" onClick={onClose} style={{ color: 'var(--ink-1)', flexShrink: 0 }}>✕</button>
        </div>
        {videoUrl ? (
          <div style={{ borderRadius: '12px', overflow: 'hidden', background: '#000' }}>
            {/* Scoreboard bar — rendered ABOVE the video so it's always visible,
                even when iOS Safari takes over the video element during playback */}
            {hasScore && (
              <div style={{
                background: 'rgba(0,0,0,0.95)',
                borderBottom: '1px solid rgba(255,255,255,0.08)',
                padding: '10px 16px',
                display: 'flex', alignItems: 'center', gap: '8px',
              }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.45)', fontWeight: 700, letterSpacing: '0.5px', marginBottom: '1px' }}>AWAY</div>
                  <div style={{ fontSize: '13px', fontWeight: 800, color: '#fff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{game.away_team}</div>
                </div>
                <div style={{ flexShrink: 0, textAlign: 'center', minWidth: '110px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
                    <span style={{ fontSize: '28px', fontWeight: 900, color: '#fff', lineHeight: 1 }}>{game.away_final_score}</span>
                    <span style={{ fontSize: '14px', color: 'rgba(255,255,255,0.35)', fontWeight: 700 }}>–</span>
                    <span style={{ fontSize: '28px', fontWeight: 900, color: '#fff', lineHeight: 1 }}>{game.home_final_score}</span>
                  </div>
                  <div style={{ marginTop: '2px', fontSize: '9px', fontWeight: 800, color: '#F97316', letterSpacing: '1px', textTransform: 'uppercase' }}>Final</div>
                </div>
                <div style={{ flex: 1, minWidth: 0, textAlign: 'right' }}>
                  <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.45)', fontWeight: 700, letterSpacing: '0.5px', marginBottom: '1px' }}>HOME</div>
                  <div style={{ fontSize: '13px', fontWeight: 800, color: '#fff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{game.home_team}</div>
                </div>
              </div>
            )}
            {/* playsInline keeps video inline on iOS so the scoreboard bar stays visible */}
            <video src={videoUrl} controls autoPlay playsInline
              style={{ width: '100%', maxHeight: '72vh', display: 'block' }} />
          </div>
        ) : (
          <div style={{ background: 'var(--surface-sunken)', border: '1px solid var(--hairline)', borderRadius: '12px', padding: '60px', textAlign: 'center', color: 'var(--ink-4)' }}>
            <div style={{ fontSize: '48px', marginBottom: '12px' }}>🎬</div>
            <div>No recording available for this game yet.</div>
          </div>
        )}
      </div>
    </div>
  );
};

// ── Analysis Reading Pane (iOS-safe bottom sheet) ─────────────────────────────
const AnalysisPaneModal = ({ game, type, onClose }: {
  game: Game & { recording?: GameVideo };
  type: 'summary' | 'ai';
  onClose: () => void;
}) => {
  const isAI      = type === 'ai';
  const label       = isAI ? '🤖 AI Coaching Analysis' : '✨ Game Recap';
  const accentColor = isAI ? '#F97316' : '#A78BFA';
  const borderColor = isAI ? 'rgba(249,115,22,0.3)' : 'rgba(139,92,246,0.3)';
  const bgAccent    = isAI ? 'rgba(249,115,22,0.06)' : 'rgba(139,92,246,0.06)';
  const content     = isAI ? (game.recording?.analysis_report ?? null) : game.ai_summary;
  const hasScore    = game.home_final_score != null && game.away_final_score != null;
  const possession  = isAI ? (game.recording?.analysis_possession ?? null) : null;

  // Inline markdown renderer for AI analysis reports
  const renderMarkdown = (text: string): React.ReactNode => {
    const lines = text.split('\n');
    const els: React.ReactNode[] = [];
    let key = 0;
    let i = 0;
    while (i < lines.length) {
      const line = lines[i];
      if (line.startsWith('## ')) {
        els.push(<h2 key={key++} style={{ fontSize: '15px', fontWeight: 800, color: 'var(--ink-1)', marginTop: i === 0 ? 0 : '22px', marginBottom: '10px', paddingBottom: '6px', borderBottom: '1px solid var(--hairline)' }}>{line.slice(3)}</h2>);
      } else if (line.startsWith('### ')) {
        els.push(<h3 key={key++} style={{ fontSize: '13px', fontWeight: 700, color: '#C9D1D9', marginTop: '14px', marginBottom: '6px' }}>{line.slice(4)}</h3>);
      } else if (/^\d+\.\s/.test(line)) {
        const numMatch = line.match(/^(\d+)\.\s(.*)/);
        if (numMatch) {
          const parts = numMatch[2].split(/\*\*([^*]+)\*\*/g);
          els.push(
            <div key={key++} style={{ display: 'flex', gap: '10px', marginBottom: '10px', alignItems: 'flex-start' }}>
              <span style={{ color: '#F97316', fontWeight: 800, fontSize: '14px', flexShrink: 0, minWidth: '18px' }}>{numMatch[1]}.</span>
              <span style={{ fontSize: '15px', lineHeight: '1.7', color: '#C9D1D9' }}>
                {parts.map((p, idx) => idx % 2 === 1 ? <strong key={idx} style={{ color: 'var(--ink-1)' }}>{p}</strong> : p)}
              </span>
            </div>
          );
        }
      } else if (line.startsWith('- ') || line.startsWith('* ')) {
        const parts = line.slice(2).split(/\*\*([^*]+)\*\*/g);
        els.push(
          <div key={key++} style={{ display: 'flex', gap: '8px', marginBottom: '8px', alignItems: 'flex-start' }}>
            <span style={{ color: '#F97316', flexShrink: 0, marginTop: '3px' }}>•</span>
            <span style={{ fontSize: '15px', lineHeight: '1.7', color: '#C9D1D9' }}>
              {parts.map((p, idx) => idx % 2 === 1 ? <strong key={idx} style={{ color: 'var(--ink-1)' }}>{p}</strong> : p)}
            </span>
          </div>
        );
      } else if (line.trim()) {
        const parts = line.split(/\*\*([^*]+)\*\*/g);
        els.push(
          <p key={key++} style={{ fontSize: '15px', lineHeight: '1.8', color: '#C9D1D9', margin: '0 0 10px' }}>
            {parts.map((p, idx) => idx % 2 === 1 ? <strong key={idx} style={{ color: 'var(--ink-1)' }}>{p}</strong> : p)}
          </p>
        );
      }
      i++;
    }
    return <>{els}</>;
  };

  // Split into paragraphs for nicer rendering (non-AI types)
  const paragraphs  = (content || '').split(/\n\n+/).map(p => p.trim()).filter(Boolean);

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', top: 0, right: 0, bottom: 0, left: 0, zIndex: 1200,
        background: 'rgba(0,0,0,0.72)',
        display: 'flex', flexDirection: 'column', justifyContent: 'flex-end',
        // desktop: center a max-width panel
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: '740px', margin: '0 auto',
          background: 'var(--surface)',
          border: `1px solid ${borderColor}`,
          borderRadius: '20px 20px 0 0',
          maxHeight: '92vh',
          display: 'flex', flexDirection: 'column',
          boxShadow: '0 -8px 40px rgba(0,0,0,0.6)',
        }}
      >
        {/* Drag handle */}
        <div style={{ paddingTop: '14px', display: 'flex', justifyContent: 'center', flexShrink: 0 }}>
          <div style={{ width: 44, height: 5, borderRadius: 3, background: 'var(--hairline-strong)' }} />
        </div>

        {/* Sticky header */}
        <div style={{
          padding: '14px 24px 16px', borderBottom: '1px solid var(--hairline)',
          flexShrink: 0, background: bgAccent,
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px' }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: '11px', fontWeight: 800, color: accentColor, textTransform: 'uppercase', letterSpacing: '1.2px', marginBottom: '5px' }}>
                {label}
              </div>
              <div style={{ fontWeight: 800, fontSize: '18px', color: 'var(--ink-1)', lineHeight: 1.2 }}>{game.title}</div>
              <div style={{ fontSize: '13px', color: 'var(--ink-3)', marginTop: '3px' }}>
                {displayTeamName(game.away_team)} <span style={{ color: 'var(--ink-4)' }}>@</span> {displayTeamName(game.home_team)}
                {hasScore && (
                  <span style={{ fontWeight: 800, color: 'var(--ink-1)', marginLeft: '8px', fontSize: '15px' }}>
                    {game.away_final_score}–{game.home_final_score}
                  </span>
                )}
                <span style={{ color: 'var(--ink-4)', marginLeft: '8px' }}>· {fmtDate(game.scheduled_at)}</span>
              </div>
            </div>
            <button
              onClick={onClose}
              style={{
                background: 'var(--hairline)', border: 'none', color: 'var(--ink-3)',
                borderRadius: '50%', width: 36, height: 36, flexShrink: 0,
                cursor: 'pointer', fontSize: '18px', display: 'flex',
                alignItems: 'center', justifyContent: 'center',
              }}
            >✕</button>
          </div>
        </div>

        {/* Scrollable body */}
        <div style={{
          flex: 1, overflowY: 'auto',
          WebkitOverflowScrolling: 'touch' as any,
          overscrollBehavior: 'contain',
          padding: '28px 24px',
        }}>
          {/* Possession bar for AI reports */}
          {isAI && possession != null && (
            <div style={{ marginBottom: '24px' }}>
              <div style={{ fontSize: '11px', fontWeight: 700, color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '8px' }}>Possession</div>
              <div style={{ display: 'flex', borderRadius: '6px', overflow: 'hidden', height: '26px', fontSize: '12px', fontWeight: 800 }}>
                <div style={{ width: `${possession}%`, background: '#F97316', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}>
                  {possession > 12 ? `${Math.round(possession)}%` : ''}
                </div>
                <div style={{ width: `${100 - possession}%`, background: 'var(--hairline)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--ink-3)' }}>
                  {100 - possession > 12 ? `${Math.round(100 - possession)}%` : ''}
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: 'var(--ink-4)', marginTop: '4px' }}>
                <span>{game.home_team} (Home)</span>
                <span>{game.away_team} (Away)</span>
              </div>
            </div>
          )}
          {content ? (
            isAI ? renderMarkdown(content) : (
              paragraphs.length > 1 ? (
                paragraphs.map((para, i) => (
                  <p key={i} style={{
                    fontSize: '16px', lineHeight: '1.9', color: '#C9D1D9',
                    margin: 0, marginBottom: i < paragraphs.length - 1 ? '20px' : 0,
                  }}>
                    {para}
                  </p>
                ))
              ) : (
                <p style={{ fontSize: '16px', lineHeight: '1.9', color: '#C9D1D9', margin: 0, whiteSpace: 'pre-wrap' }}>
                  {content}
                </p>
              )
            )
          ) : (
            <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--ink-4)' }}>
              No analysis available for this game.
            </div>
          )}
          {/* iOS home-bar spacer */}
          <div style={{ height: 'env(safe-area-inset-bottom, 24px)' }} />
        </div>
      </div>
    </div>
  );
};

// ── Game card in archive ──────────────────────────────────────────────────────
// ── Schedule Game Row ─────────────────────────────────────────────────────────
const ScheduleGameRow = ({ game, awayDisplay, homeDisplay, canEdit, onGameUpdated, editOnly }: {
  game: Game; awayDisplay: string; homeDisplay: string;
  canEdit: boolean; onGameUpdated: (updated: Game) => void;
  editOnly?: boolean;
}) => {
  const [showEdit, setShowEdit] = useState(false);
  // editOnly = render just the pencil button (used inside next-game-card)
  if (editOnly) {
    return (
      <>
        {showEdit && (
          <EditGameModal game={game} onClose={() => setShowEdit(false)} onSaved={g => { onGameUpdated(g); setShowEdit(false); }} />
        )}
        <button onClick={() => setShowEdit(true)} className="btn btn-ghost btn-sm">
          <i className="ti ti-pencil" /> Edit
        </button>
      </>
    );
  }
  return (
    <>
      {showEdit && (
        <EditGameModal game={game} onClose={() => setShowEdit(false)} onSaved={g => { onGameUpdated(g); setShowEdit(false); }} />
      )}
      <div style={{
        background: 'var(--surface)', border: `1px solid ${game.status === 'live' ? 'var(--live)' : 'var(--hairline)'}`,
        borderLeft: game.status === 'live' ? '4px solid var(--live)' : '4px solid var(--team-primary)',
        borderRadius: '10px', padding: '14px 18px',
        display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap',
      }}>
        <div style={{ flex: 1, minWidth: '180px' }}>
          <div style={{ fontWeight: 700, fontSize: '14px', marginBottom: '3px', color: 'var(--ink-1)' }}>
            {awayDisplay} <span style={{ color: 'var(--ink-4)', fontWeight: 500 }}>@</span> {homeDisplay}
          </div>
          {game.location && (
            <div style={{ fontSize: '12px', color: 'var(--ink-3)', display: 'flex', alignItems: 'center', gap: 4 }}>
              <i className="ti ti-map-pin" style={{ fontSize: 11 }} /> {game.location}
            </div>
          )}
          <div style={{ fontSize: '11px', color: 'var(--ink-4)', marginTop: '2px', fontFamily: 'monospace' }}>
            Stream ID: {game.stream_key}
          </div>
        </div>
        <div style={{ textAlign: 'right', flexShrink: 0 }}>
          <div style={{ fontWeight: 700, fontSize: '14px', color: 'var(--ink-1)' }}>
            {parseGameTime(game.scheduled_at).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
          </div>
          <div style={{ fontSize: '12px', color: 'var(--ink-3)' }}>
            {parseGameTime(game.scheduled_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
          </div>
        </div>
        <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
          <Link
            to={`/watch/${game.id}`}
            className={`btn btn-sm ${game.status === 'live' ? 'btn-primary' : 'btn-secondary'}`}
          >
            {game.status === 'live' ? '▶ Watch Live' : 'Details'}
          </Link>
          {canEdit && (
            <button onClick={() => setShowEdit(true)} className="btn btn-ghost btn-sm">
              <i className="ti ti-pencil" />
            </button>
          )}
        </div>
      </div>
    </>
  );
};

// ── Edit Game Modal ───────────────────────────────────────────────────────────
const EditGameModal = ({ game, onClose, onSaved }: {
  game: Game; onClose: () => void; onSaved: (updated: Game) => void;
}) => {
  const [title,    setTitle]    = useState(game.title    || '');
  const [homeTeam, setHomeTeam] = useState(displayTeamName(game.home_team));
  const [awayTeam, setAwayTeam] = useState(displayTeamName(game.away_team));
  const [location, setLocation] = useState(game.location || '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const save = async () => {
    setBusy(true); setError('');
    try {
      const res = await api.put(`/games/${game.id}`, {
        title:     title.trim()    || undefined,
        home_team: homeTeam.trim() || undefined,
        away_team: awayTeam.trim() || undefined,
        location:  location.trim() || undefined,
      });
      onSaved(res.data);
      onClose();
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Failed to save');
    } finally { setBusy(false); }
  };

  const fieldStyle: React.CSSProperties = { width: '100%', marginBottom: '14px' };
  const labelStyle: React.CSSProperties = { fontSize: '11px', fontWeight: 700, color: 'var(--ink-4)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '6px', display: 'block' };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.88)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 2000, padding: '24px',
    }} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{
        background: 'var(--surface-sunken)', border: '1px solid var(--hairline)', borderRadius: '16px',
        padding: '28px', width: '100%', maxWidth: '480px',
        boxShadow: '0 32px 80px rgba(0,0,0,0.6)',
      }}>
        <div style={{ fontSize: '16px', fontWeight: 800, color: 'var(--ink-1)', marginBottom: '4px' }}>
          ✏️ Edit Game Info
        </div>
        <div style={{ fontSize: '12px', color: 'var(--ink-4)', marginBottom: '20px' }}>
          Changes here update the game record permanently.
        </div>

        <div style={fieldStyle}>
          <label style={labelStyle}>Game Title</label>
          <input className="form-input" value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. vs Dynamo 2015 Girls Red" style={{ width: '100%' }} />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '14px' }}>
          <div>
            <label style={labelStyle}>Away Team</label>
            <input className="form-input" value={awayTeam} onChange={e => setAwayTeam(e.target.value)} placeholder="Away team name" style={{ width: '100%' }} />
          </div>
          <div>
            <label style={labelStyle}>Home Team</label>
            <input className="form-input" value={homeTeam} onChange={e => setHomeTeam(e.target.value)} placeholder="Home team name" style={{ width: '100%' }} />
          </div>
        </div>

        <div style={fieldStyle}>
          <label style={labelStyle}>Location / Field</label>
          <input className="form-input" value={location} onChange={e => setLocation(e.target.value)} placeholder="e.g. Grand Park Field 12" style={{ width: '100%' }} />
        </div>

        {error && (
          <div style={{ marginBottom: '14px', padding: '10px 14px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.25)', borderRadius: '8px', fontSize: '13px', color: '#F87171' }}>
            {error}
          </div>
        )}

        <div style={{ display: 'flex', gap: '8px' }}>
          <button onClick={save} disabled={busy} className="btn btn-primary" style={{ flex: 1 }}>
            {busy ? 'Saving…' : '✓ Save Changes'}
          </button>
          <button onClick={onClose} disabled={busy} className="btn btn-ghost">Cancel</button>
        </div>
      </div>
    </div>
  );
};

const ArchiveGameCard = ({ game, onWatchFilm, onViewAnalysis, onAnalyze, isAdmin, onGameUpdated }: {
  game: Game & { recording?: GameVideo };
  onWatchFilm: () => void;
  onViewAnalysis: (type: 'summary' | 'ai') => void;
  onAnalyze?: () => void;
  isAdmin?: boolean;
  onGameUpdated?: (updated: Game) => void;
}) => {
  const hasRecording   = !!game.recording;
  const hasScore       = game.home_final_score != null && game.away_final_score != null;
  const hasSummary     = !!game.ai_summary;
  const analysisStatus = game.recording?.analysis_status ?? null;
  const hasAI          = analysisStatus === 'completed';
  const isProcessing   = analysisStatus === 'processing';
  const canTrigger     = isAdmin && hasRecording && !isProcessing && !hasAI;
  const [showEdit, setShowEdit] = useState(false);

  const opponent = displayTeamName(game.away_team);

  return (
    <>
    {showEdit && onGameUpdated && (
      <EditGameModal game={game} onClose={() => setShowEdit(false)} onSaved={onGameUpdated} />
    )}
    <div style={{
      background: 'var(--surface)', border: '1px solid var(--hairline)', borderRadius: '12px',
      display: 'flex', flexDirection: 'column',
    }}>
      {/* Thumbnail — click to watch film */}
      <div
        onClick={hasRecording ? onWatchFilm : undefined}
        style={{
          height: '140px', background: 'var(--surface-sunken)', position: 'relative',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: hasRecording ? 'pointer' : 'default',
          borderRadius: '12px 12px 0 0', overflow: 'hidden',
        }}
      >
        {game.thumbnail_url ? (
          <img src={mediaUrl(game.thumbnail_url)} alt="thumbnail" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        ) : (
          <div style={{ fontSize: '44px', opacity: 0.25 }}>
            {game.sport === 'baseball' ? '⚾' : game.sport === 'softball' ? '🥎' : game.sport === 'basketball' ? '🏀' : '⚽'}
          </div>
        )}
        {hasRecording && (
          <div style={{ position: 'absolute', top: 0, right: 0, bottom: 0, left: 0, background: 'rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ width: 52, height: 52, borderRadius: '50%', background: 'rgba(249,115,22,0.92)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 12px rgba(0,0,0,0.4)' }}>
              <svg width="22" height="22" viewBox="0 0 20 20" fill="none"><polygon points="7,4 17,10 7,16" fill="white"/></svg>
            </div>
          </div>
        )}
        <div style={{ position: 'absolute', top: 8, left: 10 }}>
          <span style={{ fontSize: '10px', fontWeight: 700, color: 'var(--ink-3)', background: 'rgba(0,0,0,0.55)', padding: '2px 6px', borderRadius: 4 }}>{fmtShortDate(game.scheduled_at)}</span>
        </div>
        {game.status === 'live' && (
          <div style={{ position: 'absolute', top: 8, right: 8 }}><span className="live-badge">Live</span></div>
        )}
      </div>

      {/* Info */}
      <div style={{ padding: '12px 12px 8px', flex: 1 }}>
        <div style={{ fontWeight: 700, fontSize: '13px', marginBottom: '4px', color: 'var(--ink-1)' }}>
          vs. {opponent}
        </div>
        {hasScore ? (
          <div style={{ fontWeight: 900, fontSize: '20px', color: 'var(--ink-1)', marginBottom: '2px' }}>
            {game.home_final_score}–{game.away_final_score}
          </div>
        ) : (
          <div style={{ fontSize: '12px', color: 'var(--ink-4)' }}>Score not recorded</div>
        )}
        {game.location && (
          <div style={{ fontSize: '11px', color: 'var(--ink-4)', marginTop: '2px' }}>{game.location}</div>
        )}
      </div>

      {/* Action buttons */}
      {(hasRecording || hasSummary || hasAI || isProcessing || canTrigger || isAdmin) && (
        <div style={{ padding: '0 10px 10px', display: 'flex', flexWrap: 'wrap' }}>
          {hasRecording && (
            <button
              onClick={onWatchFilm}
              style={{
                flex: 1, minWidth: '70px', margin: '0 3px 6px 0',
                padding: '8px 6px', borderRadius: 8,
                border: '1px solid rgba(249,115,22,0.3)',
                background: 'rgba(249,115,22,0.1)', color: '#F97316',
                fontSize: '11px', fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap',
                WebkitAppearance: 'none', touchAction: 'manipulation',
              }}
            >▶ Film</button>
          )}
          {hasSummary && (
            <button
              onClick={() => onViewAnalysis('summary')}
              style={{
                flex: 1, minWidth: '70px', margin: '0 3px 6px 0',
                padding: '8px 6px', borderRadius: 8,
                border: '1px solid rgba(139,92,246,0.3)',
                background: 'rgba(139,92,246,0.1)', color: '#A78BFA',
                fontSize: '11px', fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap',
                WebkitAppearance: 'none', touchAction: 'manipulation',
              }}
            >✨ Recap</button>
          )}
          {hasAI && (
            <button
              onClick={() => onViewAnalysis('ai')}
              style={{
                flex: 1, minWidth: '70px', margin: '0 3px 6px 0',
                padding: '8px 6px', borderRadius: 8,
                border: '1px solid rgba(249,115,22,0.4)',
                background: 'rgba(249,115,22,0.12)', color: '#F97316',
                fontSize: '11px', fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap',
                WebkitAppearance: 'none', touchAction: 'manipulation',
              }}
            >🤖 AI Report</button>
          )}
          {isProcessing && (
            <div style={{
              flex: 1, minWidth: '70px', margin: '0 3px 6px 0',
              padding: '8px 6px', borderRadius: 8,
              border: '1px solid rgba(249,115,22,0.2)',
              background: 'rgba(249,115,22,0.05)', color: 'var(--ink-3)',
              fontSize: '11px', fontWeight: 700, whiteSpace: 'nowrap', textAlign: 'center',
            }}>⏳ Analyzing…</div>
          )}
          {canTrigger && (
            <button
              onClick={onAnalyze}
              style={{
                flex: 1, minWidth: '70px', margin: '0 3px 6px 0',
                padding: '8px 6px', borderRadius: 8,
                border: '1px solid rgba(168,85,247,0.3)',
                background: 'rgba(168,85,247,0.08)', color: '#A78BFA',
                fontSize: '11px', fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap',
                WebkitAppearance: 'none', touchAction: 'manipulation',
              }}
            >✨ Analyze</button>
          )}
          {isAdmin && onGameUpdated && (
            <button
              onClick={() => setShowEdit(true)}
              style={{
                flex: 1, minWidth: '70px', margin: '0 0 6px 0',
                padding: '8px 6px', borderRadius: 8,
                border: '1px solid var(--hairline)',
                background: 'transparent', color: 'var(--ink-4)',
                fontSize: '11px', fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap',
                WebkitAppearance: 'none', touchAction: 'manipulation',
              }}
            >✏️ Edit</button>
          )}
        </div>
      )}
    </div>
    </>
  );
};

// ── Calendar Subscribe button ─────────────────────────────────────────────────
const CalendarSubscribe = ({ teamId, teamName }: { teamId: string; teamName: string }) => {
  const [copied, setCopied] = useState(false);

  // webcal:// triggers "Subscribe" in Apple Calendar / Outlook
  // Use window.location.host so it works in both dev and prod
  const webcalUrl  = `webcal://${window.location.host}/api/teams/${teamId}/calendar.ics`;
  const httpsUrl   = `${window.location.origin}/api/teams/${teamId}/calendar.ics`;

  const copyUrl = () => {
    navigator.clipboard.writeText(httpsUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    });
  };

  return (
    <div className="card" style={{
      padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 12,
      flexWrap: 'wrap', marginBottom: 16, borderLeft: '4px solid var(--team-primary)',
    }}>
      <i className="ti ti-calendar" style={{ fontSize: 20, color: 'var(--team-primary)', flexShrink: 0 }} />
      <div style={{ flex: 1, minWidth: 160 }}>
        <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--ink-1)', marginBottom: 2 }}>
          Subscribe to Schedule
        </div>
        <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>
          Auto-updates in Apple Calendar, Outlook &amp; more.
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8, flexShrink: 0, flexWrap: 'wrap' }}>
        <a href={webcalUrl} className="btn btn-primary btn-sm" style={{ textDecoration: 'none' }}>
          Subscribe
        </a>
        <button
          onClick={copyUrl}
          className="btn btn-secondary btn-sm"
          title="Copy calendar URL for Google Calendar → Add calendar → From URL"
        >
          {copied ? '✓ Copied!' : 'Copy URL'}
        </button>
      </div>
    </div>
  );
};

// ── Edit Stats Panel ──────────────────────────────────────────────────────────
const EditStatsPanel = ({ team, allGames, onTeamUpdated }: {
  team: Team; allGames: Game[]; onTeamUpdated: (t: Team) => void;
}) => {
  const isBaseball = team.sport === 'baseball' || team.sport === 'softball';
  const isSoccer   = team.sport === 'soccer';

  // ── Team record override ──────────────────────────────────────────────────
  const rec = (team as any).record as { wins: number; losses: number; ties: number; override?: boolean } | undefined;
  const [recW, setRecW] = React.useState(String(rec?.wins  ?? 0));
  const [recL, setRecL] = React.useState(String(rec?.losses ?? 0));
  const [recT, setRecT] = React.useState(String(rec?.ties   ?? 0));
  const [recSaving, setRecSaving] = React.useState(false);
  const [recMsg, setRecMsg] = React.useState('');

  const saveRecord = async (clear = false) => {
    setRecSaving(true); setRecMsg('');
    try {
      await api.put(`/teams/${team.id}/record`, clear
        ? { wins: null, losses: null, ties: null }
        : { wins: Number(recW), losses: Number(recL), ties: Number(recT) });
      const r = await api.get(`/teams/${team.id}`);
      onTeamUpdated(r.data);
      setRecMsg(clear ? 'Cleared — using auto-calculated record.' : 'Saved!');
    } catch { setRecMsg('Save failed.'); }
    finally { setRecSaving(false); }
  };

  // ── Player stats by game ──────────────────────────────────────────────────
  const completedGames = [...allGames]
    .filter(g => g.status === 'completed' || g.status === 'live')
    .sort((a, b) => new Date(b.scheduled_at).getTime() - new Date(a.scheduled_at).getTime());

  const [selectedGameId, setSelectedGameId] = React.useState(completedGames[0]?.id ?? '');
  const [gameStats, setGameStats]           = React.useState<any[]>([]);
  const [statsLoading, setStatsLoading]     = React.useState(false);
  const [edits, setEdits]                   = React.useState<Record<string, Record<string, string>>>({});
  const [saving, setSaving]                 = React.useState<Record<string, boolean>>({});
  const [saved,  setSaved]                  = React.useState<Record<string, boolean>>({});

  React.useEffect(() => {
    if (!selectedGameId) return;
    setStatsLoading(true);
    setEdits({});
    api.get(`/teams/${team.id}/game-stats/${selectedGameId}`)
      .then(r => { setGameStats(r.data); })
      .catch(() => setGameStats([]))
      .finally(() => setStatsLoading(false));
  }, [selectedGameId, team.id]);

  const setField = (playerId: string, field: string, val: string) =>
    setEdits(prev => ({ ...prev, [playerId]: { ...prev[playerId], [field]: val } }));

  const getValue = (p: any, field: string) =>
    edits[p.id]?.[field] !== undefined ? edits[p.id][field] : String(p[field] ?? 0);

  const savePlayer = async (p: any) => {
    setSaving(s => ({ ...s, [p.id]: true }));
    const fields = edits[p.id] || {};
    const payload: Record<string, number> = {};
    Object.keys(fields).forEach(k => { payload[k] = Number(fields[k]) || 0; });
    // Include all current values so upsert is complete
    const allFields = isBaseball
      ? ['at_bats','hits','runs','rbis','doubles','triples','home_runs','walks','strikeouts','stolen_bases','innings_pitched','earned_runs','pitching_strikeouts','pitching_walks','errors']
      : ['goals','assists','yellow_cards','red_cards','minutes_played','shots','shots_on_target'];
    allFields.forEach(f => { if (payload[f] === undefined) payload[f] = Number(p[f] ?? 0); });
    payload.season_year = new Date(allGames.find(g => g.id === selectedGameId)?.scheduled_at || '').getFullYear() || new Date().getFullYear();
    try {
      await api.put(`/players/${p.id}/stats/${selectedGameId}`, payload);
      setGameStats(gs => gs.map(row => row.id === p.id ? { ...row, ...payload } : row));
      setEdits(prev => { const n = { ...prev }; delete n[p.id]; return n; });
      setSaved(s => ({ ...s, [p.id]: true }));
      setTimeout(() => setSaved(s => ({ ...s, [p.id]: false })), 2000);
    } catch {}
    finally { setSaving(s => ({ ...s, [p.id]: false })); }
  };

  const saveAll = async () => {
    const dirty = gameStats.filter(p => edits[p.id]);
    for (const p of dirty) await savePlayer(p);
  };

  const inputStyle: React.CSSProperties = {
    width: '52px', padding: '4px 4px', borderRadius: '6px', border: '1px solid var(--hairline)',
    background: 'var(--surface-sunken)', color: 'var(--ink-1)', fontSize: '13px', textAlign: 'center',
    fontWeight: 700,
  };

  const soccerCols = [
    { key: 'goals',          label: 'Goals' },
    { key: 'assists',        label: 'Assists' },
    { key: 'yellow_cards',   label: 'YC' },
    { key: 'red_cards',      label: 'RC' },
    { key: 'minutes_played', label: 'Min' },
    { key: 'shots',          label: 'Shots' },
    { key: 'shots_on_target',label: 'SOT' },
  ];
  const baseballCols = [
    { key: 'at_bats',           label: 'AB' },
    { key: 'hits',              label: 'H' },
    { key: 'doubles',           label: '2B' },
    { key: 'triples',           label: '3B' },
    { key: 'home_runs',         label: 'HR' },
    { key: 'rbis',              label: 'RBI' },
    { key: 'runs',              label: 'R' },
    { key: 'walks',             label: 'BB' },
    { key: 'strikeouts',        label: 'K' },
    { key: 'stolen_bases',      label: 'SB' },
    { key: 'innings_pitched',   label: 'IP' },
    { key: 'earned_runs',       label: 'ER' },
    { key: 'pitching_strikeouts', label: 'PK' },
    { key: 'pitching_walks',    label: 'PBB' },
    { key: 'errors',            label: 'E' },
  ];
  const statCols = isBaseball ? baseballCols : soccerCols;
  const hasDirty = gameStats.some(p => edits[p.id] && Object.keys(edits[p.id]).length > 0);

  const sectionHead = (label: string) => (
    <div style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '1.2px', color: 'var(--ink-4)', textTransform: 'uppercase', marginBottom: '12px' }}>
      {label}
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>

      {/* ── Team Record ────────────────────────────────────────────────────── */}
      <div style={{ background: 'var(--surface-sunken)', border: '1px solid var(--hairline)', borderRadius: '12px', padding: '20px 24px' }}>
        {sectionHead('Team Record Override')}
        <div style={{ fontSize: '13px', color: 'var(--ink-4)', marginBottom: '16px' }}>
          Override the automatically calculated win/loss/tie record shown on the stats page.
          {(team as any).record?.override && <span style={{ color: '#F97316', marginLeft: '8px' }}>Override active</span>}
        </div>
        <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
          {[{ label: 'Wins', val: recW, set: setRecW, color: '#22C55E' }, { label: 'Losses', val: recL, set: setRecL, color: '#EF4444' }, { label: 'Ties', val: recT, set: setRecT, color: '#F97316' }].map(({ label, val, set, color }) => (
            <div key={label}>
              <div style={{ fontSize: '11px', color: 'var(--ink-3)', fontWeight: 700, marginBottom: '4px', textTransform: 'uppercase' }}>{label}</div>
              <input
                type="number" min="0" value={val}
                onChange={e => set(e.target.value)}
                style={{ ...inputStyle, width: '70px', fontSize: '18px', padding: '8px', color }}
              />
            </div>
          ))}
          <div style={{ display: 'flex', gap: '8px' }}>
            <button className="btn btn-primary" disabled={recSaving} onClick={() => saveRecord(false)} style={{ height: '40px' }}>
              {recSaving ? 'Saving…' : 'Save Record'}
            </button>
            {(team as any).record?.override && (
              <button className="btn btn-secondary" disabled={recSaving} onClick={() => saveRecord(true)} style={{ height: '40px' }}>
                Clear Override
              </button>
            )}
          </div>
        </div>
        {recMsg && <div style={{ marginTop: '8px', fontSize: '13px', color: recMsg.includes('fail') ? '#EF4444' : '#22C55E' }}>{recMsg}</div>}
      </div>

      {/* ── Player Stats by Game ────────────────────────────────────────────── */}
      <div style={{ background: 'var(--surface-sunken)', border: '1px solid var(--hairline)', borderRadius: '12px', padding: '20px 24px' }}>
        {sectionHead('Player Stats by Game')}

        {completedGames.length === 0 ? (
          <div style={{ color: 'var(--ink-4)', fontSize: '13px' }}>No completed games yet.</div>
        ) : (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px', flexWrap: 'wrap' }}>
              <select
                className="form-control"
                value={selectedGameId}
                onChange={e => setSelectedGameId(e.target.value)}
                style={{ maxWidth: '380px' }}
              >
                {completedGames.map(g => (
                  <option key={g.id} value={g.id}>
                    {new Date(g.scheduled_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} — {g.title}
                  </option>
                ))}
              </select>
              {hasDirty && (
                <button className="btn btn-primary btn-sm" onClick={saveAll}>
                  Save All Changes
                </button>
              )}
            </div>

            {statsLoading ? (
              <div style={{ textAlign: 'center', padding: '40px', color: 'var(--ink-4)' }}>Loading…</div>
            ) : gameStats.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', color: 'var(--ink-4)' }}>No players on roster.</div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid var(--hairline)' }}>
                      <th style={{ textAlign: 'left', padding: '6px 12px 8px 0', color: 'var(--ink-4)', fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.8px', whiteSpace: 'nowrap' }}>Player</th>
                      {statCols.map(c => (
                        <th key={c.key} style={{ padding: '6px 4px 8px', color: 'var(--ink-4)', fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.8px', textAlign: 'center', whiteSpace: 'nowrap' }}>{c.label}</th>
                      ))}
                      <th style={{ width: '80px' }} />
                    </tr>
                  </thead>
                  <tbody>
                    {gameStats.map((p, i) => {
                      const isDirty = !!(edits[p.id] && Object.keys(edits[p.id]).length > 0);
                      return (
                        <tr key={p.id} style={{ borderBottom: i < gameStats.length - 1 ? '1px solid var(--hairline)' : 'none', background: isDirty ? 'rgba(249,115,22,0.04)' : 'transparent' }}>
                          <td style={{ padding: '8px 12px 8px 0', whiteSpace: 'nowrap' }}>
                            <span style={{ fontWeight: 700, color: 'var(--ink-1)' }}>{p.name}</span>
                            {p.number && <span style={{ color: 'var(--ink-4)', fontSize: '11px', marginLeft: '6px' }}>#{p.number}</span>}
                            {p.position && <span style={{ color: 'var(--ink-4)', fontSize: '11px', marginLeft: '4px' }}>· {p.position}</span>}
                          </td>
                          {statCols.map(c => (
                            <td key={c.key} style={{ padding: '6px 4px', textAlign: 'center' }}>
                              <input
                                type="number" min="0"
                                value={getValue(p, c.key)}
                                onChange={e => setField(p.id, c.key, e.target.value)}
                                style={inputStyle}
                              />
                            </td>
                          ))}
                          <td style={{ padding: '6px 0 6px 8px', whiteSpace: 'nowrap' }}>
                            {saved[p.id] ? (
                              <span style={{ fontSize: '12px', color: '#22C55E', fontWeight: 700 }}>✓ Saved</span>
                            ) : (
                              <button
                                className="btn btn-sm"
                                disabled={saving[p.id] || !isDirty}
                                onClick={() => savePlayer(p)}
                                style={{ opacity: isDirty ? 1 : 0.3, background: isDirty ? 'rgba(249,115,22,0.12)' : 'transparent', color: '#F97316', border: '1px solid rgba(249,115,22,0.3)' }}
                              >
                                {saving[p.id] ? '…' : 'Save'}
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

// ── Main page ─────────────────────────────────────────────────────────────────
const TeamPage = () => {
  const { teamId } = useParams<{ teamId: string }>();
  const navigate = useNavigate();
  const { isAdmin, isTeamManager, managedTeamIds } = useAuth();
  const [team, setTeam] = useState<Team | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'roster' | 'schedule' | 'archive' | 'stats' | 'league' | 'tournaments' | 'board'>('roster');
  const [watchingGame, setWatchingGame] = useState<(Game & { recording?: GameVideo }) | null>(null);
  const [analysisTarget, setAnalysisTarget] = useState<{ game: Game & { recording?: GameVideo }; type: 'summary' | 'ai' } | null>(null);

  // Team statistics
  const [teamStats, setTeamStats] = useState<TeamStatsData | null>(null);
  const [statsLoading, setStatsLoading] = useState(false);

  // Archive filters
  const [filterSearch, setFilterSearch] = useState('');

  // Record override modal
  const [showRecordEdit, setShowRecordEdit] = useState(false);
  const [recordW, setRecordW] = useState('');
  const [recordL, setRecordL] = useState('');
  const [recordT, setRecordT] = useState('');
  const [recordBusy, setRecordBusy] = useState(false);

  // Message board
  const [messages, setMessages] = useState<Message[]>([]);
  const [msgContent, setMsgContent] = useState('');
  const [msgLoading, setMsgLoading] = useState(false);
  const [msgError, setMsgError] = useState('');
  const bottomRef = useRef<HTMLDivElement>(null);

  // League tab
  const [leagueUrl, setLeagueUrl] = useState('');
  const [leagueFetching, setLeagueFetching] = useState(false);
  const [leagueError, setLeagueError] = useState('');
  const [gotsportCookie, setGotsportCookie] = useState('');
  const [showCookieHelp, setShowCookieHelp] = useState(false);
  const [gotsportEmail, setGotsportEmail] = useState('');
  const [gotsportPassword, setGotsportPassword] = useState('');
  const [screenshotUploading, setScreenshotUploading] = useState<'standings' | 'schedule' | null>(null);

  // Tournaments tab
  const [tournaments, setTournaments]         = useState<Tournament[]>([]);
  const [tournamentsLoading, setTournamentsLoading] = useState(false);
  const [showAddTournament, setShowAddTournament]   = useState(false);
  const [editingTournament, setEditingTournament]   = useState<Tournament | null>(null);

  const loadTournaments = useCallback(async () => {
    if (!teamId) return;
    setTournamentsLoading(true);
    try {
      const r = await api.get(`/tournaments?team_id=${teamId}`);
      setTournaments(r.data);
    } catch {}
    finally { setTournamentsLoading(false); }
  }, [teamId]);

  useEffect(() => {
    if (activeTab === 'tournaments') loadTournaments();
  }, [activeTab, loadTournaments]);

  const fetchMessages = useCallback(async () => {
    if (!teamId) return;
    try {
      const r = await api.get(`/teams/${teamId}/messages`);
      setMessages(r.data);
    } catch {}
  }, [teamId]);

  useEffect(() => {
    if (activeTab === 'board') {
      fetchMessages();
      const t = setInterval(fetchMessages, 30000);
      return () => clearInterval(t);
    }
  }, [activeTab, fetchMessages]);

  useEffect(() => {
    if (activeTab === 'board') {
      setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    }
  }, [messages, activeTab]);

  const postMessage = async () => {
    if (!msgContent.trim()) return;
    setMsgLoading(true); setMsgError('');
    try {
      const r = await api.post(`/teams/${teamId}/messages`, { content: msgContent.trim() });
      setMessages(m => [...m, r.data]);
      setMsgContent('');
    } catch (err: any) {
      setMsgError(err.response?.data?.error || 'Failed to post message');
    } finally { setMsgLoading(false); }
  };

  const deleteMessage = async (msgId: string) => {
    await api.delete(`/teams/${teamId}/messages/${msgId}`);
    setMessages(m => m.filter(msg => msg.id !== msgId));
  };

  useEffect(() => {
    if (!teamId) return;
    api.get(`/teams/${teamId}`)
      .then(r => setTeam(r.data))
      .finally(() => setLoading(false));
  }, [teamId]);

  useEffect(() => {
    if (activeTab !== 'stats' || !teamId) return;
    setStatsLoading(true);
    api.get(`/teams/${teamId}/stats`)
      .then(r => setTeamStats(r.data))
      .catch(() => {})
      .finally(() => setStatsLoading(false));
  }, [activeTab, teamId]);

  // ── Trigger AI analysis on a game recording ──────────────────────────────────
  const handleAnalyze = async (gameId: string, videoId: string) => {
    try {
      await api.post(`/videos/${videoId}/analyze`);
      setTeam(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          games: prev.games?.map(g =>
            g.id === gameId
              ? { ...g, recording: (g as any).recording ? { ...(g as any).recording, analysis_status: 'processing' } : (g as any).recording }
              : g
          ),
        };
      });
    } catch (e: any) {
      alert(e.response?.data?.error || 'Failed to start analysis');
    }
  };

  // ── Poll for analysis completion ──────────────────────────────────────────────
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const hasProcessing = (team?.games || []).some(g => (g as any).recording?.analysis_status === 'processing');
    if (!hasProcessing) return;
    const interval = setInterval(async () => {
      try {
        const res = await api.get(`/teams/${teamId}`);
        setTeam(res.data);
      } catch {}
    }, 10000);
    return () => clearInterval(interval);
  }, [(team?.games || []).map(g => (g as any).recording?.analysis_status).join(','), teamId]); // eslint-disable-line

  // Sponsor placements for this team
  const { seasonCard: teamSeasonCard } = useTeamSponsorPlacements(teamId);

  if (loading) return <div className="container" style={{ textAlign: 'center', padding: '80px', color: 'var(--ink-4)' }}>Loading...</div>;
  if (!team) return <div className="container" style={{ textAlign: 'center', padding: '80px', color: 'var(--loss)' }}>Team not found.</div>;

  // Whether the current user can manage this team's league settings
  const canManageLeague = isAdmin || (isTeamManager && managedTeamIds.includes(team.id));

  // Save record override
  const openRecordEdit = () => {
    const r = (team as any).record || {};
    setRecordW(String(r.wins   ?? 0));
    setRecordL(String(r.losses ?? 0));
    setRecordT(String(r.ties   ?? 0));
    setShowRecordEdit(true);
  };
  const saveRecord = async (clear = false) => {
    setRecordBusy(true);
    try {
      await api.put(`/teams/${teamId}/record`, clear
        ? { wins: null, losses: null, ties: null }
        : { wins: parseInt(recordW) || 0, losses: parseInt(recordL) || 0, ties: parseInt(recordT) || 0 }
      );
      // Refresh team data
      const res = await api.get(`/teams/${teamId}`);
      setTeam(res.data);
      setShowRecordEdit(false);
    } catch (e: any) {
      alert(e?.response?.data?.error || 'Failed to update record');
    } finally { setRecordBusy(false); }
  };
  // Show the league tab if admin/TM or if there's league data to display
  const showLeagueTab = canManageLeague || !!team.league_data;

  const allGames = (team.games || []) as (Game & { recording?: GameVideo })[];
  const pastGames = allGames.filter(g => g.status === 'completed');

  // Split scheduled games by date: live games are always upcoming; scheduled games are split
  // by whether their date is today or later (truly upcoming) vs already passed (past scheduled).
  const _todayStart = new Date(); _todayStart.setHours(0, 0, 0, 0);
  const upcomingGames = allGames
    .filter(g => g.status === 'live' || (g.status === 'scheduled' && parseGameTime(g.scheduled_at) >= _todayStart))
    .sort((a, b) => parseGameTime(a.scheduled_at).getTime() - parseGameTime(b.scheduled_at).getTime());
  // Scheduled games whose date has already passed (no recording → not in archive)
  const pastScheduledGames = allGames
    .filter(g => g.status === 'scheduled' && parseGameTime(g.scheduled_at) < _todayStart)
    .sort((a, b) => parseGameTime(b.scheduled_at).getTime() - parseGameTime(a.scheduled_at).getTime());

  const filteredArchive = pastGames.filter(g => {
    if (!filterSearch) return true;
    const q = filterSearch.toLowerCase();
    return (
      displayTeamName(g.away_team).toLowerCase().includes(q) ||
      displayTeamName(g.home_team).toLowerCase().includes(q) ||
      g.title.toLowerCase().includes(q) ||
      fmtDate(g.scheduled_at).toLowerCase().includes(q)
    );
  });

  const { wins = 0, losses = 0, ties = 0 } = team.record || {};

  // ── Seasons ──────────────────────────────────────────────────────────────────
  // After a rollover each season is its own team record; `seasons` is the chain
  // they share (oldest first). Old bookmarks land on an archived season, so it
  // always offers a way back to the current one.
  const seasons: TeamSeason[] = team.seasons || [];
  const isArchivedSeason = !!team.archived;
  const currentSeason = seasons.find(s => !s.archived && s.id !== team.id) || null;

  // ── Team theme ───────────────────────────────────────────────────────────────
  const primary    = team.primary_color    || '#0F2748';
  const secondary  = team.secondary_color  || '#FFC72C';

  const Tab = ({ id, label }: { id: typeof activeTab; label: string }) => (
    <button
      onClick={() => setActiveTab(id)}
      className={`team-subnav-tab${activeTab === id ? ' active' : ''}`}
    >
      {label}
    </button>
  );

  return (
    <TeamThemeProvider primary={primary} secondary={secondary}>
      {/* ── Team Hero ── */}
      <div className="team-hero">
        <div className="team-hero-inner">
          {/* Logo / crest */}
          <div className="team-crest">
            {team.logo_url
              ? <img src={mediaUrl(team.logo_url)} alt={team.name} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
              : team.name.charAt(0)}
          </div>

          <div className="team-hero-body">
            <div className="team-hero-name">{team.name}</div>
            <div style={{ fontSize: '12px', opacity: 0.65, marginBottom: 6, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span>{team.sport.charAt(0).toUpperCase() + team.sport.slice(1)} · {seasonName(team)}</span>
              {isArchivedSeason && (
                <span style={{
                  fontSize: 10, fontWeight: 800, letterSpacing: '0.04em', textTransform: 'uppercase',
                  background: 'rgba(255,255,255,0.16)', border: '1px solid rgba(255,255,255,0.25)',
                  borderRadius: 999, padding: '2px 8px', opacity: 1,
                }}>
                  Past season
                </span>
              )}
              {seasons.length > 1 && (
                <select
                  value={team.id}
                  onChange={e => navigate(`/teams/${e.target.value}`)}
                  title="Switch season"
                  style={{
                    fontSize: 11, fontWeight: 700, color: '#fff', cursor: 'pointer',
                    background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.22)',
                    borderRadius: 6, padding: '3px 6px',
                  }}
                >
                  {[...seasons].reverse().map(s => (
                    <option key={s.id} value={s.id} style={{ color: '#111' }}>
                      {seasonName(s)}{s.archived ? '' : ' · current'}
                    </option>
                  ))}
                </select>
              )}
            </div>
            <div className="team-hero-record">
              <div>
                <div className="stat-num">{wins}</div>
                <div className="stat-label">W</div>
              </div>
              <div className="team-hero-divider" />
              <div>
                <div className="stat-num">{losses}</div>
                <div className="stat-label">L</div>
              </div>
              {ties > 0 && <>
                <div className="team-hero-divider" />
                <div>
                  <div className="stat-num">{ties}</div>
                  <div className="stat-label">T</div>
                </div>
              </>}
              {canManageLeague && (
                <button onClick={openRecordEdit} style={{
                  marginLeft: 6, fontSize: '11px', fontWeight: 600,
                  color: 'rgba(255,255,255,0.7)', background: 'rgba(255,255,255,0.12)',
                  border: '1px solid rgba(255,255,255,0.2)', borderRadius: 6,
                  padding: '3px 8px', cursor: 'pointer',
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                }}>
                  <i className="ti ti-pencil" style={{ fontSize: 10 }} />
                  {(team as any).record?.override ? 'Overridden · Edit' : 'Edit Record'}
                </button>
              )}
            </div>
          </div>

          {/* Back link — top-right of hero on desktop */}
          <div style={{ flexShrink: 0, alignSelf: 'flex-start' }}>
            <Link to="/teams" style={{
              fontSize: 13, fontWeight: 600, color: 'rgba(255,255,255,0.65)',
              display: 'inline-flex', alignItems: 'center', gap: 4,
              background: 'rgba(255,255,255,0.1)', borderRadius: 8,
              padding: '6px 12px', border: '1px solid rgba(255,255,255,0.15)',
            }}>
              <i className="ti ti-arrow-left" style={{ fontSize: 12 }} /> All Teams
            </Link>
          </div>
        </div>
      </div>

      {/* Archived-season notice — bookmarks and old links land here after a rollover */}
      {isArchivedSeason && (
        <div style={{
          background: 'var(--surface-sunken)', borderBottom: '1px solid var(--hairline)',
          padding: '10px 0', fontSize: 13, color: 'var(--ink-2)',
        }}>
          <div className="container" style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <i className="ti ti-archive" />
            <span>You're viewing the <strong>{seasonName(team)}</strong> season — records, stats and highlights are final.</span>
            {currentSeason && (
              <Link to={`/teams/${currentSeason.id}`} style={{ fontWeight: 700, color: 'var(--sr-teal, #0D4F4C)' }}>
                Go to {seasonName(currentSeason)} →
              </Link>
            )}
          </div>
        </div>
      )}

      {/* ── Team Subnav ── */}
      <div className="team-subnav">
        <div className="team-subnav-inner">
          <Tab id="roster"      label={`Roster (${team.players?.length || 0})`} />
          <Tab id="schedule"    label={`Schedule (${upcomingGames.length + pastScheduledGames.length})`} />
          <Tab id="archive"     label={`Results (${pastGames.length})`} />
          <Tab id="stats"       label="Stats" />
          {showLeagueTab && <Tab id="league" label="League" />}
          <Tab id="tournaments" label={`Tournaments${tournaments.length ? ` (${tournaments.length})` : ''}`} />
          <Tab id="board"       label="Board" />
        </div>
      </div>

      {/* ── Page body ── */}
      <div className="container" style={{ paddingTop: 24, paddingBottom: 96 }}>
        {/* Edit Stats shortcut — admin/manager only */}
        {(isAdmin || (isTeamManager && managedTeamIds.includes(team.id))) && (
          <div style={{ marginBottom: 16 }}>
            <Link to={`/teams/${team.id}/edit-stats`} className="btn btn-secondary btn-sm">
              <i className="ti ti-pencil" /> Edit Stats &amp; Delete Games
            </Link>
          </div>
        )}

      {/* ── Roster Tab ── */}
      {activeTab === 'roster' && (
        <div>
          {!team.players?.length ? (
            <div style={{ textAlign: 'center', padding: '60px', color: 'var(--ink-4)' }}>No players added yet.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {team.players.map((player: Player) => (
                <Link key={player.id} to={`/players/${player.id}`} style={{ textDecoration: 'none' }}>
                  <div style={{
                    background: 'var(--surface)', border: '1px solid var(--hairline)', borderRadius: 10,
                    padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 16,
                    transition: 'border-color 0.15s, box-shadow 0.15s',
                  }}
                    onMouseEnter={e => { (e.currentTarget as HTMLElement).style.borderColor = 'var(--hairline-strong)'; (e.currentTarget as HTMLElement).style.boxShadow = '0 2px 8px rgba(0,0,0,0.06)'; }}
                    onMouseLeave={e => { (e.currentTarget as HTMLElement).style.borderColor = 'var(--hairline)'; (e.currentTarget as HTMLElement).style.boxShadow = 'none'; }}
                  >
                    {/* Photo or number avatar */}
                    <div style={{
                      width: 44, height: 44, borderRadius: '50%', flexShrink: 0,
                      background: player.photo_url ? 'transparent' : primary,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: player.photo_url ? undefined : '15px', fontWeight: 900, color: '#fff',
                      overflow: 'hidden', border: '1px solid var(--hairline)',
                    }}>
                      {player.photo_url
                        ? <img src={mediaUrl(player.photo_url)} alt={player.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        : `#${player.number ?? '?'}`}
                    </div>

                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--ink-1)' }}>{player.name}</div>
                      <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>
                        {player.position || 'Position unknown'}
                        {player.grade ? ` · ${player.grade}` : ''}
                        {player.age ? ` · Age ${player.age}` : ''}
                      </div>
                    </div>

                    {player.number != null && (
                      <div style={{
                        fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 900,
                        color: 'var(--team-primary)', flexShrink: 0,
                      }}>
                        #{player.number}
                      </div>
                    )}

                    <i className="ti ti-chevron-right" style={{ fontSize: 14, color: 'var(--ink-4)' }} />
                  </div>
                </Link>
              ))}
            </div>
          )}
          {/* Season sponsor card */}
          {teamSeasonCard && (
            <div style={{ marginTop: 24 }}>
              <SeasonSponsorCard sponsor={teamSeasonCard} />
            </div>
          )}
        </div>
      )}

      {/* ── Schedule Tab ── */}
      {activeTab === 'schedule' && (
        <div>
          {/* Calendar subscribe row */}
          <CalendarSubscribe teamId={teamId!} teamName={team.name} />

          {(() => {
            const canEdit = isAdmin || (isTeamManager && managedTeamIds.includes(team.id));
            return (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {/* ── Upcoming games ── */}
                {upcomingGames.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '40px', color: 'var(--ink-4)' }}>No upcoming games scheduled.</div>
                ) : (() => {
                  const [nextGame, ...restGames] = upcomingGames;
                  const nextDate = parseGameTime(nextGame.scheduled_at);
                  return (
                    <>
                      {/* Next game hero card */}
                      <div>
                        <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--ink-4)', marginBottom: 8 }}>Next Game</div>
                        <div className="next-game-card">
                          <div className="next-game-day">
                            <div className="wk">{nextDate.toLocaleDateString('en-US', { weekday: 'short' })}</div>
                            <div className="num">{nextDate.getDate()}</div>
                            <div className="mon">{nextDate.toLocaleDateString('en-US', { month: 'short' })}</div>
                          </div>
                          <div className="next-game-matchup">
                            <div className="time">
                              {nextGame.status === 'live'
                                ? <span className="live-badge"><span className="live-dot" />LIVE</span>
                                : nextDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                            </div>
                            <div className="teams">
                              {displayTeamName(nextGame.away_team)} <span style={{ opacity: 0.4, fontWeight: 400, fontSize: '0.75em' }}>vs</span> {displayTeamName(nextGame.home_team)}
                            </div>
                            {nextGame.location && (
                              <div className="venue">
                                <i className="ti ti-map-pin" style={{ fontSize: 12 }} /> {nextGame.location}
                              </div>
                            )}
                            <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
                              <Link to={`/watch/${nextGame.id}`} className={`btn btn-sm ${nextGame.status === 'live' ? 'btn-primary' : 'btn-secondary'}`}>
                                {nextGame.status === 'live' ? '▶ Watch Live' : 'Details'}
                              </Link>
                              {canEdit && (
                                <ScheduleGameRow
                                  key={nextGame.id}
                                  game={nextGame}
                                  awayDisplay={displayTeamName(nextGame.away_team)}
                                  homeDisplay={displayTeamName(nextGame.home_team)}
                                  canEdit={canEdit}
                                  onGameUpdated={updated => setTeam(t => t ? { ...t, games: t.games?.map(g => g.id === updated.id ? { ...g, ...updated } : g) as any } : t)}
                                  editOnly
                                />
                              )}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Remaining upcoming games */}
                      {restGames.length > 0 && (
                        <div>
                          <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--ink-4)', marginBottom: 8 }}>Upcoming</div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                            {restGames.map(game => (
                              <ScheduleGameRow
                                key={game.id}
                                game={game}
                                awayDisplay={displayTeamName(game.away_team)}
                                homeDisplay={displayTeamName(game.home_team)}
                                canEdit={canEdit}
                                onGameUpdated={updated => setTeam(t => t ? { ...t, games: t.games?.map(g => g.id === updated.id ? { ...g, ...updated } : g) as any } : t)}
                              />
                            ))}
                          </div>
                        </div>
                      )}
                    </>
                  );
                })()}

                {/* ── Past games (scheduled status but date already passed) ── */}
                {pastScheduledGames.length > 0 && (
                  <div style={{ marginTop: 8 }}>
                    <div style={{
                      fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em',
                      color: 'var(--ink-4)', marginBottom: 8,
                      display: 'flex', alignItems: 'center', gap: 8,
                    }}>
                      Past Games
                      <span style={{ fontWeight: 500, textTransform: 'none', letterSpacing: 0, fontSize: 11, color: 'var(--ink-4)', opacity: 0.7 }}>
                        — results not yet recorded
                      </span>
                    </div>
                    <div style={{
                      background: 'var(--surface-sunken)', border: '1px solid var(--hairline)', borderRadius: 10,
                      overflow: 'hidden', opacity: 0.8,
                    }}>
                      {pastScheduledGames.map((game, i) => {
                        const d = parseGameTime(game.scheduled_at);
                        return (
                          <div key={game.id} style={{
                            padding: '11px 16px', display: 'flex', alignItems: 'center', gap: 14,
                            borderBottom: i < pastScheduledGames.length - 1 ? '1px solid var(--hairline)' : 'none',
                          }}>
                            <div style={{ flexShrink: 0, textAlign: 'center', minWidth: 42 }}>
                              <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--ink-4)', textTransform: 'uppercase' }}>
                                {d.toLocaleDateString('en-US', { month: 'short' })}
                              </div>
                              <div style={{ fontSize: 18, fontWeight: 900, color: 'var(--ink-3)', lineHeight: 1 }}>{d.getDate()}</div>
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--ink-2)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {displayTeamName(game.away_team)} <span style={{ color: 'var(--ink-4)', fontWeight: 400 }}>vs</span> {displayTeamName(game.home_team)}
                              </div>
                              {game.location && (
                                <div style={{ fontSize: 11, color: 'var(--ink-4)', marginTop: 2 }}>
                                  <i className="ti ti-map-pin" style={{ fontSize: 10 }} /> {game.location}
                                </div>
                              )}
                            </div>
                            <div style={{ flexShrink: 0, display: 'flex', gap: 6 }}>
                              <Link to={`/watch/${game.id}`} className="btn btn-ghost btn-sm">Details</Link>
                              {canEdit && (
                                <ScheduleGameRow
                                  key={game.id}
                                  game={game}
                                  awayDisplay={displayTeamName(game.away_team)}
                                  homeDisplay={displayTeamName(game.home_team)}
                                  canEdit={canEdit}
                                  onGameUpdated={updated => setTeam(t => t ? { ...t, games: t.games?.map(g => g.id === updated.id ? { ...g, ...updated } : g) as any } : t)}
                                  editOnly
                                />
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })()}
        </div>
      )}

      {/* ── Archive Tab ── */}
      {activeTab === 'archive' && (
        <div>
          {/* Search/filter */}
          <div style={{ marginBottom: '16px' }}>
            <input
              className="form-control"
              placeholder="Search by opponent or date..."
              value={filterSearch}
              onChange={e => setFilterSearch(e.target.value)}
              style={{ maxWidth: '360px' }}
            />
          </div>

          {!filteredArchive.length ? (
            <div style={{ textAlign: 'center', padding: '60px', color: 'var(--ink-4)' }}>
              {filterSearch ? 'No games match your search.' : 'No completed games yet.'}
            </div>
          ) : (
            <div>
              {/* Result rows — ESPN-style result list */}
              <div className="card" style={{ marginBottom: 16 }}>
                {filteredArchive.map(game => {
                  const hscore = game.home_final_score ?? null;
                  const ascore = game.away_final_score ?? null;
                  const hasScore = hscore !== null && ascore !== null;
                  // Determine outcome from team perspective (if we can tell)
                  const homeIsUs = (game.home_team || '').toLowerCase().includes((team.name || '').toLowerCase());
                  const ourScore = homeIsUs ? hscore : ascore;
                  const theirScore = homeIsUs ? ascore : hscore;
                  const outcome = !hasScore ? null
                    : ourScore! > theirScore! ? 'W'
                    : ourScore! < theirScore! ? 'L' : 'T';
                  const opp = displayTeamName(homeIsUs ? game.away_team : game.home_team);
                  return (
                    <div key={game.id} className="result-row" style={{ padding: '12px 16px' }}>
                      {outcome && <div className={`result-badge ${outcome}`}>{outcome}</div>}
                      <div className="result-opp" style={{ flex: 1 }}>
                        <div><span className="vs">vs</span><span className="name">{opp}</span></div>
                        <div className="date">{fmtShortDate(game.scheduled_at)}{game.location ? ` · ${game.location}` : ''}</div>
                      </div>
                      {hasScore && (
                        <div className="result-score">{ourScore}–{theirScore}</div>
                      )}
                      <div style={{ display: 'flex', gap: 6 }}>
                        {game.recording && (
                          <button onClick={() => setWatchingGame(game)} className="btn btn-secondary btn-sm">
                            <i className="ti ti-player-play" /> Film
                          </button>
                        )}
                        {game.recording?.analysis_report && (
                          <button onClick={() => setAnalysisTarget({ game, type: 'ai' })} className="btn btn-ghost btn-sm">
                            AI
                          </button>
                        )}
                        {game.ai_summary && (
                          <button onClick={() => setAnalysisTarget({ game, type: 'summary' })} className="btn btn-ghost btn-sm">
                            Recap
                          </button>
                        )}
                        {game.recording && !game.recording.analysis_report && game.recording.analysis_status !== 'processing' && (isAdmin || (isTeamManager && managedTeamIds.includes(team.id))) && (
                          <button onClick={() => handleAnalyze(game.id, game.recording!.id)} className="btn btn-ghost btn-sm">
                            <i className="ti ti-sparkles" />
                          </button>
                        )}
                        {game.recording?.analysis_status === 'processing' && (
                          <span style={{ fontSize: 12, color: 'var(--ink-4)', alignSelf: 'center' }}>Analyzing…</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Statistics Tab ── */}
      {activeTab === 'stats' && (
        <div>
          {statsLoading ? (
            <div style={{ textAlign: 'center', padding: '60px', color: 'var(--ink-4)' }}>Loading statistics…</div>
          ) : !teamStats ? (
            <div style={{ textAlign: 'center', padding: '60px', color: 'var(--ink-4)' }}>No statistics available yet.</div>
          ) : (() => {
            const isBaseball   = team.sport === 'baseball' || team.sport === 'softball';
            const isBasketball = team.sport === 'basketball';
            const sportEmoji   = team.sport === 'baseball' ? '⚾' : team.sport === 'softball' ? '🥎' : team.sport === 'basketball' ? '🏀' : '⚽';

            // Baseball/softball summary cards show runs; soccer shows goals
            const summarySoccer = [
              { label: 'Wins',         value: teamStats.record.wins,   color: '#22C55E' },
              { label: 'Losses',       value: teamStats.record.losses, color: '#EF4444' },
              { label: 'Ties',         value: teamStats.record.ties,   color: '#F97316' },
              { label: 'Goals For',    value: teamStats.totals.goalsFor,     color: '#22C55E' },
              { label: 'Goals Against',value: teamStats.totals.goalsAgainst, color: '#EF4444' },
              { label: 'Goal Diff',    value: teamStats.totals.goalDifference > 0
                ? `+${teamStats.totals.goalDifference}` : String(teamStats.totals.goalDifference),
                color: teamStats.totals.goalDifference > 0 ? '#22C55E' : teamStats.totals.goalDifference < 0 ? '#EF4444' : 'var(--ink-1)' },
              { label: 'Yellow Cards', value: teamStats.totals.total_yellow_cards, color: '#FBBF24' },
            ];
            const summaryBaseball = [
              { label: 'Games Played', value: teamStats.record.played, color: 'var(--ink-1)' },
              { label: 'Wins',         value: teamStats.record.wins,   color: '#22C55E' },
              { label: 'Losses',       value: teamStats.record.losses, color: '#EF4444' },
              { label: 'Runs For',     value: teamStats.totals.goalsFor,     color: '#22C55E' },
              { label: 'Runs Against', value: teamStats.totals.goalsAgainst, color: '#EF4444' },
              { label: 'Run Diff',     value: teamStats.totals.goalDifference > 0
                ? `+${teamStats.totals.goalDifference}` : String(teamStats.totals.goalDifference),
                color: teamStats.totals.goalDifference > 0 ? '#22C55E' : teamStats.totals.goalDifference < 0 ? '#EF4444' : 'var(--ink-1)' },
              { label: 'Team Hits',    value: teamStats.players.reduce((s: number, p: any) => s + (p.hits || 0), 0), color: '#F97316' },
              { label: 'Team HRs',     value: teamStats.players.reduce((s: number, p: any) => s + (p.home_runs || 0), 0), color: '#EF4444' },
            ];
            // Basketball: just record cards (no individual stats tracked)
            const summaryBasketball = [
              { label: 'Games Played', value: teamStats.record.played, color: 'var(--ink-1)' },
              { label: 'Wins',         value: teamStats.record.wins,   color: '#22C55E' },
              { label: 'Losses',       value: teamStats.record.losses, color: '#EF4444' },
              { label: 'Pts For',      value: teamStats.totals.goalsFor,      color: '#22C55E' },
              { label: 'Pts Against',  value: teamStats.totals.goalsAgainst,  color: '#EF4444' },
              { label: 'Pt Diff',      value: teamStats.totals.goalDifference > 0
                ? `+${teamStats.totals.goalDifference}` : String(teamStats.totals.goalDifference),
                color: teamStats.totals.goalDifference > 0 ? '#22C55E' : teamStats.totals.goalDifference < 0 ? '#EF4444' : 'var(--ink-1)' },
            ];
            const summaryCards = isBaseball ? summaryBaseball : isBasketball ? summaryBasketball : summarySoccer;

            // Player leaderboard columns
            const leaderboardCols = isBaseball
              ? [
                  { key: 'games_played', label: 'GP',  color: (v: number) => 'var(--ink-3)' },
                  { key: 'at_bats',      label: 'AB',  color: (v: number) => v > 0 ? 'var(--ink-1)' : 'var(--ink-4)' },
                  { key: 'hits',         label: 'H',   color: (v: number) => v > 0 ? '#22C55E' : 'var(--ink-4)' },
                  { key: 'rbis',         label: 'RBI', color: (v: number) => v > 0 ? '#F97316' : 'var(--ink-4)' },
                  { key: 'home_runs',    label: 'HR',  color: (v: number) => v > 0 ? '#EF4444' : 'var(--ink-4)' },
                  { key: 'stolen_bases', label: 'SB',  color: (v: number) => v > 0 ? '#A78BFA' : 'var(--ink-4)' },
                ]
              : [
                  { key: 'goals',         label: '⚽',  color: (v: number) => v > 0 ? '#22C55E' : 'var(--ink-4)' },
                  { key: 'assists',       label: '🎯',  color: (v: number) => v > 0 ? '#F97316' : 'var(--ink-4)' },
                  { key: 'yellow_cards',  label: '🟨',  color: (v: number) => v > 0 ? '#FBBF24' : 'var(--ink-4)' },
                  { key: 'red_cards',     label: '🟥',  color: (v: number) => v > 0 ? '#EF4444' : 'var(--ink-4)' },
                ];

            const hasStats = isBasketball
              ? false  // Basketball doesn't track individual player stats
              : isBaseball
                ? teamStats.players.some((p: any) => p.games_played > 0 || p.hits > 0 || p.at_bats > 0)
                : teamStats.players.some((p: any) => p.goals > 0 || p.assists > 0);

            const gridCols = `1fr ${leaderboardCols.map(() => '52px').join(' ')}`;

            return (
            <>
              {/* ── Season Summary ── */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: 10, marginBottom: 20 }}>
                {summaryCards.map(({ label, value, color }) => (
                  <div key={label} className="card" style={{ padding: '16px 12px', textAlign: 'center' }}>
                    <div style={{ fontFamily: 'var(--font-display)', fontSize: 32, fontWeight: 900, color, lineHeight: 1 }}>{value}</div>
                    <div style={{ fontSize: 10, color: 'var(--ink-4)', fontWeight: 700, marginTop: 6, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</div>
                  </div>
                ))}
              </div>

              {/* ── Player Leaderboard ── */}
              {hasStats ? (
                <div className="card" style={{ overflow: 'hidden' }}>
                  <div style={{ padding: '14px 16px', borderBottom: '1px solid var(--hairline)', display: 'flex', alignItems: 'center', gap: 8 }}>
                    <i className="ti ti-chart-bar" style={{ color: 'var(--team-primary)' }} />
                    <span style={{ fontWeight: 800, fontSize: 15, color: 'var(--ink-1)' }}>Player Statistics</span>
                  </div>
                  {/* Header row */}
                  <div style={{ display: 'grid', gridTemplateColumns: gridCols, padding: '8px 16px', borderBottom: '1px solid var(--hairline)', fontSize: 10, fontWeight: 700, color: 'var(--ink-4)', textTransform: 'uppercase', letterSpacing: '0.8px' }}>
                    <div>Player</div>
                    {leaderboardCols.map(c => <div key={c.key} style={{ textAlign: 'center' }}>{c.label}</div>)}
                  </div>
                  {teamStats.players
                    .filter((p: any) => isBaseball ? (p.games_played > 0 || p.hits > 0) : (p.goals > 0 || p.assists > 0))
                    .map((p: any, i: number, arr: any[]) => (
                    <div key={p.id} style={{ display: 'grid', gridTemplateColumns: gridCols, padding: '10px 16px', borderBottom: i < arr.length - 1 ? '1px solid var(--hairline)' : 'none', alignItems: 'center', background: i % 2 === 0 ? 'transparent' : 'var(--surface-muted)' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                        <div style={{ width: 32, height: 32, borderRadius: '50%', flexShrink: 0, background: p.photo_url ? 'transparent' : primary, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 900, color: '#fff', overflow: 'hidden', border: '1px solid var(--hairline)' }}>
                          {p.photo_url
                            ? <img src={mediaUrl(p.photo_url)} alt={p.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            : `#${p.number ?? '?'}`}
                        </div>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--ink-1)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.name}</div>
                          {p.position && <div style={{ fontSize: 11, color: 'var(--ink-4)' }}>{p.position}</div>}
                        </div>
                      </div>
                      {leaderboardCols.map(c => (
                        <div key={c.key} style={{ textAlign: 'center', fontSize: 13, fontWeight: p[c.key] > 0 ? 800 : 400, color: c.color(p[c.key]) }}>
                          {c.key === 'games_played' ? <span style={{ color: 'var(--ink-4)', fontWeight: 600 }}>{p[c.key]}</span> : p[c.key]}
                        </div>
                      ))}
                    </div>
                  ))}
                  {/* Footer legend */}
                  <div style={{ padding: '10px 16px', borderTop: '1px solid var(--hairline)', fontSize: 11, color: 'var(--ink-4)', display: 'flex', gap: 16, flexWrap: 'wrap', background: 'var(--surface-muted)' }}>
                    {isBaseball ? (
                      <><span>GP = Games Played</span><span>AB = At Bats</span><span>H = Hits</span><span>RBI = Runs Batted In</span><span>HR = Home Runs</span><span>SB = Stolen Bases</span></>
                    ) : (
                      <><span>{sportEmoji} = Goals</span><span>🎯 = Assists</span><span>🟨 = Yellow Cards</span><span>🟥 = Red Cards</span></>
                    )}
                  </div>
                </div>
              ) : (
                <div className="card" style={{ padding: 48, textAlign: 'center', color: 'var(--ink-4)' }}>
                  <i className="ti ti-chart-bar" style={{ fontSize: 36, display: 'block', marginBottom: 12, opacity: 0.3 }} />
                  <div>No player statistics recorded yet. Stats are logged during live games.</div>
                </div>
              )}
            </>
            );
          })()}
        </div>
      )}

      {/* ── League Tab ── */}
      {activeTab === 'league' && showLeagueTab && (() => {
        const ld = team.league_data as LeagueData | null | undefined;
        const standingCols: string[] = ld?.standings?.length
          ? Object.keys(ld.standings[0]).filter(k => k !== 'team')
          : [];

        const uploadScreenshots = async (files: FileList, section: 'standings' | 'schedule') => {
          setScreenshotUploading(section);
          setLeagueError('');
          try {
            const form = new FormData();
            Array.from(files).forEach(f => form.append('screenshots', f));
            form.append('section', section);
            const r = await api.post(`/teams/${teamId}/league-screenshot`, form, {
              headers: { 'Content-Type': 'multipart/form-data' },
            });
            setTeam(prev => prev ? { ...prev, ...r.data } : prev);
          } catch (err: any) {
            setLeagueError(err.response?.data?.error || 'Upload failed');
          } finally {
            setScreenshotUploading(null);
          }
        };

        const fetchLeague = async () => {
          const urlToFetch = leagueUrl.trim() || team.league_url || '';
          if (!urlToFetch) return;
          setLeagueFetching(true);
          setLeagueError('');
          try {
            const body: any = { url: urlToFetch };
            if (gotsportEmail.trim()) body.gotsport_email = gotsportEmail.trim();
            if (gotsportPassword.trim()) body.gotsport_password = gotsportPassword.trim();
            if (gotsportCookie.trim()) body.gotsport_cookie = gotsportCookie.trim();
            const r = await api.post(`/teams/${teamId}/fetch-league`, body);
            setTeam(prev => prev ? { ...prev, ...r.data } : prev);
            setLeagueUrl('');
          } catch (err: any) {
            setLeagueError(err.response?.data?.error || 'Failed to fetch league data');
          } finally {
            setLeagueFetching(false);
          }
        };

        return (
          <div>
            {/* Admin/TM controls */}
            {canManageLeague && (
              <div className="card" style={{ padding: '18px 20px', marginBottom: 24 }}>
                <div style={{ fontWeight: 800, fontSize: '14px', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span>🌐</span> League Page URL
                </div>
                <div style={{ fontSize: '12px', color: 'var(--ink-3)', marginBottom: '14px' }}>
                  Paste a link to your league's standings &amp; schedule page. The AI will extract and display the data below.
                </div>
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                  <input
                    className="form-control"
                    style={{ flex: 1, minWidth: '220px' }}
                    placeholder={team.league_url || 'https://example.com/league/standings'}
                    value={leagueUrl}
                    onChange={e => setLeagueUrl(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') fetchLeague(); }}
                  />
                  <button
                    className="btn btn-primary"
                    onClick={fetchLeague}
                    disabled={leagueFetching || (!leagueUrl.trim() && !team.league_url)}
                  >
                    {leagueFetching ? '⏳ Fetching…' : '✨ Fetch League Data'}
                  </button>
                </div>
                {/* GotSport Login (auto-bypasses CAPTCHA for gotsport.com URLs) */}
                {/gotsport\.com/i.test(leagueUrl || team.league_url || '') && (
                  <div style={{ marginTop: '14px', borderTop: '1px solid var(--hairline)', paddingTop: '14px' }}>
                    <div style={{ fontWeight: 700, fontSize: '13px', marginBottom: '4px' }}>
                      🔑 GotSport Login <span style={{ fontWeight: 400, color: 'var(--ink-4)', fontSize: '12px' }}>— saves your credentials so syncs happen automatically</span>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginTop: '8px' }}>
                      <input
                        className="form-control"
                        type="email"
                        placeholder="GotSport email"
                        value={gotsportEmail}
                        onChange={e => setGotsportEmail(e.target.value)}
                      />
                      <input
                        className="form-control"
                        type="password"
                        placeholder="GotSport password"
                        value={gotsportPassword}
                        onChange={e => setGotsportPassword(e.target.value)}
                      />
                    </div>
                    <div style={{ fontSize: '11px', color: 'var(--ink-4)', marginTop: '6px' }}>
                      Stored securely · used only to fetch league data · syncs automatically every 6 hours
                    </div>

                    {/* Document upload — separate sections for standings and schedule */}
                    <div style={{ marginTop: '14px', borderTop: '1px solid var(--hairline)', paddingTop: '14px' }}>
                      <div style={{ fontWeight: 700, fontSize: '13px', marginBottom: '4px' }}>
                        📄 Upload Documents <span style={{ fontWeight: 400, color: 'var(--ink-4)', fontSize: '12px' }}>— photos or PDFs, extracted by AI</span>
                      </div>
                      <div style={{ fontSize: '11px', color: 'var(--ink-4)', marginBottom: '12px' }}>
                        Upload each section separately — standings and schedule are saved independently and won't overwrite each other.
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        {/* Standings upload */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <div style={{ fontSize: '12px', color: 'var(--ink-3)', fontWeight: 700, width: '80px', flexShrink: 0 }}>Standings</div>
                          <label style={{
                            display: 'inline-flex', alignItems: 'center', gap: '6px', cursor: screenshotUploading ? 'default' : 'pointer',
                            background: screenshotUploading === 'standings' ? 'var(--hairline)' : 'var(--surface-sunken)',
                            border: '1px dashed var(--ink-4)', borderRadius: '6px',
                            padding: '8px 14px', fontSize: '13px', color: screenshotUploading === 'standings' ? 'var(--ink-4)' : 'var(--ink-1)',
                          }}>
                            {screenshotUploading === 'standings' ? '⏳ Extracting…' : '📁 Choose file'}
                            <input type="file" accept="image/*,application/pdf" multiple style={{ display: 'none' }}
                              disabled={!!screenshotUploading}
                              onChange={e => e.target.files?.length && uploadScreenshots(e.target.files, 'standings')} />
                          </label>
                        </div>
                        {/* Schedule upload */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <div style={{ fontSize: '12px', color: 'var(--ink-3)', fontWeight: 700, width: '80px', flexShrink: 0 }}>Schedule</div>
                          <label style={{
                            display: 'inline-flex', alignItems: 'center', gap: '6px', cursor: screenshotUploading ? 'default' : 'pointer',
                            background: screenshotUploading === 'schedule' ? 'var(--hairline)' : 'var(--surface-sunken)',
                            border: '1px dashed var(--ink-4)', borderRadius: '6px',
                            padding: '8px 14px', fontSize: '13px', color: screenshotUploading === 'schedule' ? 'var(--ink-4)' : 'var(--ink-1)',
                          }}>
                            {screenshotUploading === 'schedule' ? '⏳ Extracting…' : '📁 Choose file'}
                            <input type="file" accept="image/*,application/pdf" multiple style={{ display: 'none' }}
                              disabled={!!screenshotUploading}
                              onChange={e => e.target.files?.length && uploadScreenshots(e.target.files, 'schedule')} />
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                {leagueError && (
                  <div style={{ color: '#F87171', fontSize: '13px', marginTop: '10px' }}>{leagueError}</div>
                )}
                {team.league_data_updated_at && (
                  <div style={{ fontSize: '11px', color: 'var(--ink-4)', marginTop: '10px' }}>
                    Last updated: {new Date(team.league_data_updated_at).toLocaleString()}
                    {team.league_url && (
                      <> · <a href={team.league_url} target="_blank" rel="noreferrer" style={{ color: '#F97316' }}>View source →</a></>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* League data display */}
            {!ld ? (
              <div style={{ textAlign: 'center', padding: '60px', color: 'var(--ink-4)' }}>
                <div style={{ fontSize: '40px', marginBottom: '12px' }}>🏆</div>
                <div>{canManageLeague ? 'Enter a league URL above to populate standings and schedule.' : 'No league data available yet.'}</div>
              </div>
            ) : (
              <>
                {ld.league_name && (
                  <div style={{ fontWeight: 900, fontSize: '20px', marginBottom: '20px', color: 'var(--ink-1)' }}>
                    🏆 {ld.league_name}
                  </div>
                )}

                {/* Standings table */}
                {ld.standings && ld.standings.length > 0 && (
                  <div style={{
                    background: 'var(--surface)', border: '1px solid var(--hairline)', borderRadius: '12px',
                    overflow: 'hidden', marginBottom: '24px',
                  }}>
                    <div style={{ padding: '14px 16px', borderBottom: '1px solid var(--hairline)', fontWeight: 800, fontSize: '15px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span>📊</span> Standings
                    </div>
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                        <thead>
                          <tr style={{ borderBottom: '1px solid var(--hairline)' }}>
                            {standingCols.includes('rank') && (
                              <th style={{ padding: '8px 12px', textAlign: 'center', color: 'var(--ink-4)', fontWeight: 700, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px', whiteSpace: 'nowrap' }}>#</th>
                            )}
                            <th style={{ padding: '8px 12px', textAlign: 'left', color: 'var(--ink-4)', fontWeight: 700, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px', whiteSpace: 'nowrap' }}>Team</th>
                            {standingCols.filter(c => c !== 'rank').map(col => (
                              <th key={col} style={{ padding: '8px 12px', textAlign: 'center', color: 'var(--ink-4)', fontWeight: 700, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px', whiteSpace: 'nowrap' }}>
                                {col.toUpperCase()}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {ld.standings.map((row, i) => {
                            const isOurTeam = row.team?.toLowerCase().includes(team.name.toLowerCase()) ||
                              team.name.toLowerCase().includes((row.team || '').toLowerCase().split(' ')[0]);
                            return (
                              <tr key={i} style={{
                                borderBottom: i < ld.standings.length - 1 ? '1px solid var(--hairline)' : 'none',
                                background: isOurTeam ? 'rgba(249,115,22,0.06)' : 'transparent',
                              }}>
                                {standingCols.includes('rank') && (
                                  <td style={{ padding: '10px 12px', textAlign: 'center', color: 'var(--ink-3)', fontWeight: 600 }}>
                                    {row.rank ?? i + 1}
                                  </td>
                                )}
                                <td style={{ padding: '10px 12px', fontWeight: isOurTeam ? 800 : 600, color: isOurTeam ? '#F97316' : 'var(--ink-1)', whiteSpace: 'nowrap' }}>
                                  {isOurTeam && <span style={{ marginRight: '6px' }}>⭐</span>}
                                  {row.team}
                                </td>
                                {standingCols.filter(c => c !== 'rank').map(col => (
                                  <td key={col} style={{
                                    padding: '10px 12px', textAlign: 'center',
                                    fontWeight: col === 'pts' ? 800 : 500,
                                    color: col === 'pts' ? 'var(--ink-1)' : 'var(--ink-3)',
                                  }}>
                                    {row[col] ?? '—'}
                                  </td>
                                ))}
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Schedule / Results */}
                {ld.schedule && ld.schedule.length > 0 && (
                  <div style={{
                    background: 'var(--surface)', border: '1px solid var(--hairline)', borderRadius: '12px',
                    overflow: 'hidden',
                  }}>
                    <div style={{ padding: '14px 16px', borderBottom: '1px solid var(--hairline)', fontWeight: 800, fontSize: '15px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span>📅</span> Schedule &amp; Results
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      {ld.schedule.map((game, i) => {
                        const isCompleted = game.status === 'completed' || !!game.result;
                        const involvesOurTeam =
                          game.home_team?.toLowerCase().includes(team.name.toLowerCase().split(' ')[0]) ||
                          game.away_team?.toLowerCase().includes(team.name.toLowerCase().split(' ')[0]);
                        return (
                          <div key={i} style={{
                            padding: '12px 16px',
                            borderBottom: i < ld.schedule.length - 1 ? '1px solid var(--hairline)' : 'none',
                            display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap',
                            background: involvesOurTeam ? 'rgba(249,115,22,0.04)' : 'transparent',
                          }}>
                            {game.date && (
                              <div style={{ minWidth: '70px', fontSize: '12px', color: 'var(--ink-4)', flexShrink: 0 }}>
                                {game.date}
                              </div>
                            )}
                            <div style={{ flex: 1, minWidth: '180px' }}>
                              <span style={{ fontWeight: involvesOurTeam && game.home_team?.toLowerCase().includes(team.name.toLowerCase().split(' ')[0]) ? 800 : 600, color: 'var(--ink-1)', fontSize: '13px' }}>
                                {game.home_team}
                              </span>
                              <span style={{ color: 'var(--ink-4)', margin: '0 8px', fontSize: '12px' }}>vs</span>
                              <span style={{ fontWeight: involvesOurTeam && game.away_team?.toLowerCase().includes(team.name.toLowerCase().split(' ')[0]) ? 800 : 600, color: 'var(--ink-1)', fontSize: '13px' }}>
                                {game.away_team}
                              </span>
                            </div>
                            <div style={{ flexShrink: 0, textAlign: 'right' }}>
                              {game.result ? (
                                <span style={{ fontWeight: 800, fontSize: '14px', color: 'var(--ink-1)' }}>{game.result}</span>
                              ) : (
                                <span style={{
                                  fontSize: '11px', fontWeight: 700, padding: '3px 8px', borderRadius: '6px',
                                  background: isCompleted ? 'var(--surface-muted)' : 'rgba(249,115,22,0.15)',
                                  color: isCompleted ? 'var(--ink-4)' : '#F97316',
                                }}>
                                  {isCompleted ? 'Final' : 'Upcoming'}
                                </span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        );
      })()}

      {/* ── Tournaments Tab ── */}
      {activeTab === 'tournaments' && (() => {
        const canManage = isAdmin || (isTeamManager && managedTeamIds?.includes(teamId!));

        // ── Add / Edit Tournament Modal ─────────────────────────────────────
        const TournamentModal = ({ existing }: { existing?: Tournament }) => {
          const [url,          setUrl]          = React.useState(existing?.url ?? '');
          const [scheduleUrl,  setScheduleUrl]  = React.useState(existing?.schedule_url ?? '');
          const [bracketUrl,   setBracketUrl]   = React.useState(existing?.bracket_url ?? '');
          const [name,         setName]         = React.useState(existing?.name ?? '');
          const [startDate,    setStartDate]    = React.useState(existing?.start_date ?? '');
          const [endDate,      setEndDate]      = React.useState(existing?.end_date ?? '');
          const [location,     setLocation]     = React.useState(existing?.location ?? '');
          const [format,       setFormat]       = React.useState(existing?.format ?? '');
          const [division,     setDivision]     = React.useState(existing?.division ?? '');
          const [description,  setDescription]  = React.useState(existing?.description ?? '');
          const [notes,        setNotes]        = React.useState(existing?.notes ?? '');
          const [fetching, setFetching] = React.useState(false);
          const [saving,   setSaving]   = React.useState(false);
          const [err,      setErr]      = React.useState('');
          const [refetched, setRefetched] = React.useState(false);

          const handleRefetch = async () => {
            const target = url || existing?.url;
            if (!target) { setErr('Enter a tournament URL first'); return; }
            setFetching(true); setErr('');
            try {
              const r = await api.post(`/tournaments/${existing!.id}/refetch`, { url: target });
              const t: Tournament = r.data;
              setName(t.name); setStartDate(t.start_date ?? ''); setEndDate(t.end_date ?? '');
              setLocation(t.location ?? ''); setFormat(t.format ?? ''); setDivision(t.division ?? '');
              setDescription(t.description ?? '');
              if (t.schedule_url && !scheduleUrl) setScheduleUrl(t.schedule_url);
              if (t.bracket_url  && !bracketUrl)  setBracketUrl(t.bracket_url);
              setRefetched(true);
            } catch (e: any) { setErr(e?.response?.data?.error ?? 'AI fetch failed'); }
            finally { setFetching(false); }
          };

          const handleSave = async () => {
            setSaving(true); setErr('');
            try {
              if (existing) {
                await api.put(`/tournaments/${existing.id}`, {
                  name, url, schedule_url: scheduleUrl, bracket_url: bracketUrl,
                  start_date: startDate, end_date: endDate, location, format,
                  division, description, notes,
                });
              } else {
                await api.post('/tournaments', {
                  team_id: teamId, url, schedule_url: scheduleUrl, bracket_url: bracketUrl,
                  name: name || undefined,
                });
              }
              await loadTournaments();
              setShowAddTournament(false);
              setEditingTournament(null);
            } catch (e: any) { setErr(e?.response?.data?.error ?? 'Save failed'); }
            finally { setSaving(false); }
          };

          const isNew = !existing;
          return (
            <div className="modal-overlay" onClick={() => { setShowAddTournament(false); setEditingTournament(null); }}>
              <div className="modal" style={{ maxWidth: '600px', maxHeight: '90vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                  <h2>🏆 {isNew ? 'Add Tournament' : 'Edit Tournament'}</h2>
                  <button className="modal-close" onClick={() => { setShowAddTournament(false); setEditingTournament(null); }}>✕</button>
                </div>

                {err && <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: '8px', padding: '10px 14px', marginBottom: '14px', color: '#EF4444', fontSize: '13px' }}>{err}</div>}

                {/* URL row */}
                <div className="form-group">
                  <label>Tournament Page URL</label>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <input className="form-control" style={{ flex: 1 }} placeholder="https://tournament-site.com/..." value={url} onChange={e => setUrl(e.target.value)} />
                    {existing && (
                      <button className="btn btn-secondary" style={{ whiteSpace: 'nowrap', fontSize: '12px' }} disabled={fetching} onClick={handleRefetch}>
                        {fetching ? '⏳ Fetching...' : '🤖 Re-fetch AI'}
                      </button>
                    )}
                  </div>
                  {isNew && <p style={{ fontSize: '12px', color: 'var(--ink-3)', marginTop: '6px', marginBottom: 0 }}>Paste the main tournament page URL — AI will extract dates, location, format, and more automatically.</p>}
                  {refetched && <p style={{ fontSize: '12px', color: '#22C55E', marginTop: '4px', marginBottom: 0 }}>✓ AI updated the fields below — review and save.</p>}
                </div>

                {/* If editing, show all fields; if new, show minimal (AI fills the rest) */}
                {(existing || name) && (
                  <>
                    <div className="form-group">
                      <label>Tournament Name</label>
                      <input className="form-control" placeholder="e.g. State Cup 2026" value={name} onChange={e => setName(e.target.value)} />
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
                      <div className="form-group">
                        <label>Start Date</label>
                        <input type="date" className="form-control" value={startDate} onChange={e => setStartDate(e.target.value)} />
                      </div>
                      <div className="form-group">
                        <label>End Date</label>
                        <input type="date" className="form-control" value={endDate} onChange={e => setEndDate(e.target.value)} />
                      </div>
                    </div>
                    <div className="form-group">
                      <label>Location</label>
                      <input className="form-control" placeholder="e.g. Grand Park, Westfield IN" value={location} onChange={e => setLocation(e.target.value)} />
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
                      <div className="form-group">
                        <label>Format</label>
                        <input className="form-control" placeholder="e.g. Pool Play + Bracket" value={format} onChange={e => setFormat(e.target.value)} />
                      </div>
                      <div className="form-group">
                        <label>Division / Age Group</label>
                        <input className="form-control" placeholder="e.g. U13 Girls Gold" value={division} onChange={e => setDivision(e.target.value)} />
                      </div>
                    </div>
                    <div className="form-group">
                      <label>Description</label>
                      <textarea className="form-control" rows={3} style={{ resize: 'vertical' }} placeholder="Tournament overview..." value={description} onChange={e => setDescription(e.target.value)} />
                    </div>
                  </>
                )}

                {/* Schedule & Bracket URLs — always shown */}
                <div className="form-group">
                  <label>Schedule URL <span style={{ color: 'var(--ink-4)', fontWeight: 400 }}>(optional — AI will try to find this)</span></label>
                  <input className="form-control" placeholder="https://..." value={scheduleUrl} onChange={e => setScheduleUrl(e.target.value)} />
                </div>
                <div className="form-group">
                  <label>Bracket URL <span style={{ color: 'var(--ink-4)', fontWeight: 400 }}>(optional — AI will try to find this)</span></label>
                  <input className="form-control" placeholder="https://..." value={bracketUrl} onChange={e => setBracketUrl(e.target.value)} />
                </div>

                {existing && (
                  <div className="form-group">
                    <label>Notes <span style={{ color: 'var(--ink-4)', fontWeight: 400 }}>(internal)</span></label>
                    <textarea className="form-control" rows={2} style={{ resize: 'vertical' }} placeholder="Team notes about this tournament..." value={notes} onChange={e => setNotes(e.target.value)} />
                  </div>
                )}

                <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '8px' }}>
                  <button className="btn btn-secondary" onClick={() => { setShowAddTournament(false); setEditingTournament(null); }}>Cancel</button>
                  <button className="btn btn-primary" disabled={saving || fetching} onClick={handleSave}>
                    {saving ? (isNew ? '⏳ Creating...' : '⏳ Saving...') : (isNew ? '🤖 Fetch & Create' : '💾 Save Changes')}
                  </button>
                </div>
              </div>
            </div>
          );
        };

        // ── Tournament card ──────────────────────────────────────────────────
        const TournamentCard = ({ t: initialT }: { t: Tournament }) => {
          const [t, setT] = React.useState(initialT);
          const [cardTab, setCardTab] = React.useState<'overview'|'schedule'|'bracket'>('overview');
          const [deleting,         setDeleting]         = React.useState(false);
          const [fetchingSched,    setFetchingSched]    = React.useState(false);
          const [fetchingBracket,  setFetchingBracket]  = React.useState(false);
          const [schedErr,         setSchedErr]         = React.useState('');
          const [bracketErr,       setBracketErr]       = React.useState('');

          const schedData: TournamentScheduleData | null = t.schedule_data ? JSON.parse(t.schedule_data) : null;
          const bracketData: TournamentBracketData | null = t.bracket_data ? JSON.parse(t.bracket_data) : null;

          const hasSchedule = !!(schedData?.games?.length || schedData?.standings?.length);
          const hasBracket  = !!(bracketData?.rounds?.length);

          const fmt = (d: string | null) => d ? new Date(d + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : null;
          const dateStr = (() => {
            if (!t.start_date && !t.end_date) return null;
            if (t.start_date && t.end_date && t.start_date !== t.end_date)
              return `${fmt(t.start_date)} – ${fmt(t.end_date)}`;
            return fmt(t.start_date || t.end_date);
          })();

          const handleDelete = async () => {
            if (!window.confirm(`Delete "${t.name}"?`)) return;
            setDeleting(true);
            try { await api.delete(`/tournaments/${t.id}`); await loadTournaments(); }
            catch { setDeleting(false); }
          };

          const fetchSchedule = async () => {
            setFetchingSched(true); setSchedErr('');
            try {
              const r = await api.post(`/tournaments/${t.id}/fetch-schedule`, { url: t.schedule_url });
              setT(r.data.tournament);
              setCardTab('schedule');
            } catch (e: any) { setSchedErr(e?.response?.data?.error ?? 'AI fetch failed'); }
            finally { setFetchingSched(false); }
          };

          const fetchBracket = async () => {
            setFetchingBracket(true); setBracketErr('');
            try {
              const r = await api.post(`/tournaments/${t.id}/fetch-bracket`, { url: t.bracket_url });
              setT(r.data.tournament);
              setCardTab('bracket');
            } catch (e: any) { setBracketErr(e?.response?.data?.error ?? 'AI fetch failed'); }
            finally { setFetchingBracket(false); }
          };

          const cardTabBtn = (id: typeof cardTab, label: string, hasData: boolean) => (
            <button onClick={() => setCardTab(id)} style={{
              padding: '6px 14px', borderRadius: '7px', border: 'none', cursor: 'pointer',
              fontWeight: 700, fontSize: '12px', transition: 'all 0.15s',
              background: cardTab === id ? '#F97316' : 'transparent',
              color: cardTab === id ? '#fff' : hasData ? 'var(--ink-3)' : 'var(--ink-4)',
              position: 'relative',
            }}>
              {label}
              {hasData && cardTab !== id && <span style={{ marginLeft: '4px', fontSize: '9px', background: 'rgba(249,115,22,0.2)', color: '#F97316', borderRadius: '4px', padding: '1px 4px' }}>✓</span>}
            </button>
          );

          // ── Pool standings block ─────────────────────────────────────────
          const PoolStandings = ({ pools }: { pools: TournamentScheduleData['standings'] }) => {
            if (!pools?.length) return null;
            return (
              <>
                {pools.map((pool, pi) => {
                  if (!pool.teams?.length) return null;
                  const cols = Object.keys(pool.teams[0]).filter(k => k !== 'team' && k !== 'rank');
                  return (
                    <div key={pi} style={{ marginBottom: '16px' }}>
                      {pool.pool && <div style={{ fontSize: '12px', fontWeight: 800, color: '#F97316', letterSpacing: '0.5px', marginBottom: '8px', textTransform: 'uppercase' }}>{pool.pool}</div>}
                      <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                          <thead>
                            <tr style={{ borderBottom: '1px solid var(--hairline)' }}>
                              {cols.includes('rank') && <th style={{ padding: '6px 8px', textAlign: 'center', color: 'var(--ink-4)', fontWeight: 700, fontSize: '10px', textTransform: 'uppercase' }}>#</th>}
                              <th style={{ padding: '6px 8px', textAlign: 'left', color: 'var(--ink-4)', fontWeight: 700, fontSize: '10px', textTransform: 'uppercase' }}>Team</th>
                              {cols.filter(c => c !== 'rank').map(c => (
                                <th key={c} style={{ padding: '6px 8px', textAlign: 'center', color: 'var(--ink-4)', fontWeight: 700, fontSize: '10px', textTransform: 'uppercase' }}>{c.toUpperCase()}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {pool.teams.map((row, ri) => {
                              const isOurs = row.team?.toLowerCase().includes(team!.name.toLowerCase().split(' ')[0]);
                              return (
                                <tr key={ri} style={{ borderBottom: ri < pool.teams.length - 1 ? '1px solid var(--hairline)' : 'none', background: isOurs ? 'rgba(249,115,22,0.06)' : 'transparent' }}>
                                  {cols.includes('rank') && <td style={{ padding: '8px', textAlign: 'center', color: 'var(--ink-4)' }}>{row.rank ?? ri + 1}</td>}
                                  <td style={{ padding: '8px', fontWeight: isOurs ? 800 : 600, color: isOurs ? '#F97316' : 'var(--ink-1)', whiteSpace: 'nowrap' }}>{isOurs && '⭐ '}{row.team}</td>
                                  {cols.filter(c => c !== 'rank').map(c => (
                                    <td key={c} style={{ padding: '8px', textAlign: 'center', color: c === 'pts' || c === 'pct' ? 'var(--ink-1)' : 'var(--ink-3)', fontWeight: c === 'pts' || c === 'pct' ? 800 : 400 }}>{(row as any)[c] ?? '—'}</td>
                                  ))}
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  );
                })}
              </>
            );
          };

          return (
            <div style={{ background: 'var(--surface)', border: '1px solid var(--hairline)', borderRadius: '14px', overflow: 'hidden' }}>
              {/* Header */}
              <div style={{ padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '4px' }}>
                      <span style={{ fontSize: '18px', fontWeight: 800, color: 'var(--ink-1)' }}>🏆 {t.name}</span>
                      {t.ai_fetched_at && <span style={{ fontSize: '9px', background: 'rgba(249,115,22,0.15)', color: '#F97316', borderRadius: '4px', padding: '2px 6px', fontWeight: 700 }}>AI</span>}
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', fontSize: '12px', color: 'var(--ink-3)' }}>
                      {dateStr   && <span>📅 {dateStr}</span>}
                      {t.location && <span>📍 {t.location}</span>}
                      {t.division && <span>👥 {t.division}</span>}
                      {t.format   && <span>🎯 {t.format}</span>}
                    </div>
                  </div>
                  {canManage && (
                    <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                      <button onClick={() => setEditingTournament(t)} style={{ padding: '5px 10px', borderRadius: '6px', border: '1px solid var(--hairline)', background: 'transparent', color: 'var(--ink-3)', cursor: 'pointer', fontSize: '12px' }}>✏️</button>
                      <button onClick={handleDelete} disabled={deleting} style={{ padding: '5px 10px', borderRadius: '6px', border: '1px solid rgba(239,68,68,0.3)', background: 'transparent', color: '#EF4444', cursor: 'pointer', fontSize: '12px', opacity: deleting ? 0.5 : 1 }}>🗑</button>
                    </div>
                  )}
                </div>

                {/* External links row */}
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  {t.url && <a href={t.url} target="_blank" rel="noopener noreferrer" style={{ padding: '5px 12px', borderRadius: '6px', background: 'var(--surface-muted)', color: 'var(--ink-3)', fontWeight: 600, fontSize: '12px', textDecoration: 'none' }}>🌐 Site</a>}
                  {t.schedule_url && <a href={t.schedule_url} target="_blank" rel="noopener noreferrer" style={{ padding: '5px 12px', borderRadius: '6px', background: 'rgba(59,130,246,0.1)', color: '#3B82F6', fontWeight: 600, fontSize: '12px', textDecoration: 'none' }}>🔗 Schedule</a>}
                  {t.bracket_url  && <a href={t.bracket_url}  target="_blank" rel="noopener noreferrer" style={{ padding: '5px 12px', borderRadius: '6px', background: 'rgba(249,115,22,0.1)', color: '#F97316', fontWeight: 600, fontSize: '12px', textDecoration: 'none' }}>🔗 Bracket</a>}
                </div>

                {/* Sub-tabs */}
                <div style={{ display: 'flex', gap: '2px', background: 'var(--surface-sunken)', borderRadius: '8px', padding: '3px' }}>
                  {cardTabBtn('overview', 'Overview', !!t.description)}
                  {cardTabBtn('schedule', `Schedule${schedData?.games?.length ? ` (${schedData.games.length})` : ''}`, hasSchedule)}
                  {cardTabBtn('bracket',  `Bracket${bracketData?.rounds?.length ? ` (${bracketData.rounds.length}R)` : ''}`, hasBracket)}
                </div>
              </div>

              {/* Tab content */}
              <div style={{ padding: '0 20px 18px', borderTop: '1px solid var(--hairline)' }}>

                {/* ── Overview ── */}
                {cardTab === 'overview' && (
                  <div style={{ paddingTop: '14px' }}>
                    {t.description
                      ? <p style={{ fontSize: '14px', color: 'var(--ink-3)', lineHeight: 1.65, margin: 0 }}>{t.description}</p>
                      : <p style={{ fontSize: '13px', color: 'var(--ink-4)', margin: 0 }}>No description available.</p>
                    }
                    {canManage && t.notes && (
                      <div style={{ marginTop: '12px', fontSize: '12px', color: 'var(--ink-4)', fontStyle: 'italic' }}>📝 {t.notes}</div>
                    )}
                  </div>
                )}

                {/* ── Schedule ── */}
                {cardTab === 'schedule' && (
                  <div style={{ paddingTop: '14px' }}>
                    {/* Manager fetch controls */}
                    {canManage && (
                      <div style={{ marginBottom: '16px', display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                        <button className="btn btn-secondary" style={{ fontSize: '12px', padding: '6px 12px' }} disabled={fetchingSched || !t.schedule_url} onClick={fetchSchedule}>
                          {fetchingSched ? '⏳ Fetching…' : '🤖 Fetch Schedule'}
                        </button>
                        {!t.schedule_url && <span style={{ fontSize: '12px', color: 'var(--ink-4)' }}>Add a Schedule URL via ✏️ Edit first</span>}
                        {t.schedule_fetched_at && <span style={{ fontSize: '11px', color: 'var(--ink-4)' }}>Updated {new Date(t.schedule_fetched_at).toLocaleDateString()}</span>}
                        {schedErr && <span style={{ fontSize: '12px', color: '#EF4444' }}>{schedErr}</span>}
                      </div>
                    )}

                    {schedData ? (
                      <>
                        {/* Pool standings */}
                        {schedData.standings?.some(p => p.teams?.length > 0) && (
                          <div style={{ marginBottom: '20px' }}>
                            <div style={{ fontSize: '13px', fontWeight: 800, color: 'var(--ink-1)', marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                              📊 Standings
                            </div>
                            <PoolStandings pools={schedData.standings} />
                          </div>
                        )}

                        {/* Game list */}
                        {schedData.games?.length > 0 && (
                          <div>
                            <div style={{ fontSize: '13px', fontWeight: 800, color: 'var(--ink-1)', marginBottom: '10px' }}>📅 Games</div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                              {schedData.games.map((g, i) => {
                                const involvesOurs = team && (
                                  g.home_team?.toLowerCase().includes(team.name.toLowerCase().split(' ')[0]) ||
                                  g.away_team?.toLowerCase().includes(team.name.toLowerCase().split(' ')[0])
                                );
                                const isCompleted = g.status === 'completed' || !!g.result;
                                return (
                                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 10px', borderRadius: '7px', background: involvesOurs ? 'rgba(249,115,22,0.06)' : 'transparent', flexWrap: 'wrap' }}>
                                    {g.round && <span style={{ fontSize: '10px', color: '#F97316', fontWeight: 700, minWidth: '60px' }}>{g.round}</span>}
                                    <span style={{ fontSize: '11px', color: 'var(--ink-4)', flexShrink: 0 }}>{[g.date, g.time, g.field].filter(Boolean).join(' · ')}</span>
                                    <span style={{ flex: 1, fontSize: '13px', fontWeight: involvesOurs ? 800 : 500, color: involvesOurs ? 'var(--ink-1)' : 'var(--ink-3)', minWidth: '140px' }}>
                                      {g.home_team} <span style={{ color: 'var(--ink-4)' }}>vs</span> {g.away_team}
                                    </span>
                                    <span style={{ flexShrink: 0, fontSize: '13px', fontWeight: 800, color: g.result ? 'var(--ink-1)' : isCompleted ? 'var(--ink-4)' : '#F97316' }}>
                                      {g.result || (isCompleted ? 'Final' : 'Upcoming')}
                                    </span>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </>
                    ) : (
                      <div style={{ textAlign: 'center', padding: '30px', color: 'var(--ink-4)' }}>
                        {canManage
                          ? t.schedule_url
                            ? 'Click "🤖 Fetch Schedule" above to load the schedule.'
                            : 'Add a Schedule URL via ✏️ Edit, then click Fetch.'
                          : 'No schedule data yet.'}
                      </div>
                    )}
                  </div>
                )}

                {/* ── Bracket ── */}
                {cardTab === 'bracket' && (
                  <div style={{ paddingTop: '14px' }}>
                    {/* Manager fetch controls */}
                    {canManage && (
                      <div style={{ marginBottom: '16px', display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                        <button className="btn btn-secondary" style={{ fontSize: '12px', padding: '6px 12px' }} disabled={fetchingBracket || !t.bracket_url} onClick={fetchBracket}>
                          {fetchingBracket ? '⏳ Fetching…' : '🤖 Fetch Bracket'}
                        </button>
                        {!t.bracket_url && <span style={{ fontSize: '12px', color: 'var(--ink-4)' }}>Add a Bracket URL via ✏️ Edit first</span>}
                        {t.bracket_fetched_at && <span style={{ fontSize: '11px', color: 'var(--ink-4)' }}>Updated {new Date(t.bracket_fetched_at).toLocaleDateString()}</span>}
                        {bracketErr && <span style={{ fontSize: '12px', color: '#EF4444' }}>{bracketErr}</span>}
                      </div>
                    )}

                    {bracketData?.rounds?.length ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                        {bracketData.rounds.map((round, ri) => (
                          <div key={ri}>
                            <div style={{ fontSize: '12px', fontWeight: 800, color: '#F97316', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px' }}>
                              {round.round}
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                              {round.games?.map((g, gi) => {
                                const oursT1 = team && g.team1?.toLowerCase().includes(team.name.toLowerCase().split(' ')[0]);
                                const oursT2 = team && g.team2?.toLowerCase().includes(team.name.toLowerCase().split(' ')[0]);
                                const isPlayed = g.winner !== null || (g.score1 !== null && g.score2 !== null);
                                return (
                                  <div key={gi} style={{ background: 'var(--surface-sunken)', borderRadius: '8px', padding: '10px 14px', display: 'flex', alignItems: 'center', gap: '12px', border: (oursT1 || oursT2) ? '1px solid rgba(249,115,22,0.2)' : '1px solid transparent', flexWrap: 'wrap' }}>
                                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '4px', minWidth: '140px' }}>
                                      {/* Team 1 */}
                                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                                        <span style={{ fontSize: '13px', fontWeight: g.winner === g.team1 ? 800 : 500, color: g.winner === g.team1 ? 'var(--ink-1)' : 'var(--ink-3)' }}>
                                          {oursT1 && '⭐ '}{g.team1}
                                        </span>
                                        {g.score1 !== null && <span style={{ fontSize: '15px', fontWeight: 900, color: g.winner === g.team1 ? 'var(--ink-1)' : 'var(--ink-4)' }}>{g.score1}</span>}
                                      </div>
                                      {/* Team 2 */}
                                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                                        <span style={{ fontSize: '13px', fontWeight: g.winner === g.team2 ? 800 : 500, color: g.winner === g.team2 ? 'var(--ink-1)' : 'var(--ink-3)' }}>
                                          {oursT2 && '⭐ '}{g.team2}
                                        </span>
                                        {g.score2 !== null && <span style={{ fontSize: '15px', fontWeight: 900, color: g.winner === g.team2 ? 'var(--ink-1)' : 'var(--ink-4)' }}>{g.score2}</span>}
                                      </div>
                                    </div>
                                    <div style={{ flexShrink: 0, textAlign: 'right' }}>
                                      {isPlayed
                                        ? <span style={{ fontSize: '10px', fontWeight: 700, color: '#22C55E', background: 'rgba(34,197,94,0.1)', borderRadius: '4px', padding: '2px 6px' }}>Final</span>
                                        : <span style={{ fontSize: '10px', fontWeight: 700, color: '#F97316', background: 'rgba(249,115,22,0.1)', borderRadius: '4px', padding: '2px 6px' }}>TBD</span>
                                      }
                                      {[g.date, g.time, g.field].filter(Boolean).length > 0 && (
                                        <div style={{ fontSize: '10px', color: 'var(--ink-4)', marginTop: '3px' }}>{[g.date, g.time, g.field].filter(Boolean).join(' · ')}</div>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div style={{ textAlign: 'center', padding: '30px', color: 'var(--ink-4)' }}>
                        {canManage
                          ? t.bracket_url
                            ? 'Click "🤖 Fetch Bracket" above to load the bracket.'
                            : 'Add a Bracket URL via ✏️ Edit, then click Fetch.'
                          : 'No bracket data yet.'}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        };

        return (
          <div>
            {/* Header + Add button */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px', flexWrap: 'wrap', gap: '10px' }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 800 }}>Tournaments</h3>
                <p style={{ margin: '4px 0 0', fontSize: '13px', color: 'var(--ink-3)' }}>
                  Tournament pages are built automatically by AI from the tournament URL.
                </p>
              </div>
              {canManage && (
                <button className="btn btn-primary" onClick={() => setShowAddTournament(true)}>
                  + Add Tournament
                </button>
              )}
            </div>

            {/* Loading */}
            {tournamentsLoading && (
              <div style={{ textAlign: 'center', padding: '60px', color: 'var(--ink-4)' }}>Loading tournaments…</div>
            )}

            {/* Empty state */}
            {!tournamentsLoading && tournaments.length === 0 && (
              <div style={{ textAlign: 'center', padding: '60px 20px' }}>
                <div style={{ fontSize: '48px', marginBottom: '14px' }}>🏆</div>
                <div style={{ fontSize: '16px', fontWeight: 700, color: 'var(--ink-3)', marginBottom: '8px' }}>No tournaments added yet</div>
                {canManage ? (
                  <p style={{ color: 'var(--ink-4)', fontSize: '14px' }}>
                    Click <strong style={{ color: '#F97316' }}>+ Add Tournament</strong> and paste the tournament URL — AI will build the page for you.
                  </p>
                ) : (
                  <p style={{ color: 'var(--ink-4)', fontSize: '14px' }}>Tournament info will appear here when added by the team manager.</p>
                )}
              </div>
            )}

            {/* Tournament cards */}
            {!tournamentsLoading && tournaments.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {tournaments.map(t => <TournamentCard key={t.id} t={t} />)}
              </div>
            )}

            {/* Modals */}
            {showAddTournament && <TournamentModal />}
            {editingTournament && <TournamentModal existing={editingTournament} />}
          </div>
        );
      })()}

      {/* ── Board Tab ── */}
      {activeTab === 'board' && (
        <div style={{ maxWidth: '680px' }}>
          {/* Messages list */}
          <div style={{
            background: 'var(--surface-sunken)', border: '1px solid var(--hairline)', borderRadius: '12px',
            padding: '16px', marginBottom: '16px', minHeight: '200px',
            display: 'flex', flexDirection: 'column', gap: '12px',
          }}>
            {messages.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', color: 'var(--ink-4)' }}>
                No messages yet. Be the first to post!
              </div>
            ) : (
              messages.map(msg => (
                <div key={msg.id} style={{
                  background: 'var(--surface)', border: '1px solid var(--hairline)', borderRadius: 10,
                  padding: '12px 14px',
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div style={{
                        width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
                        background: 'var(--team-primary)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 11, fontWeight: 800, color: 'var(--team-primary-on)',
                      }}>
                        {msg.username.charAt(0).toUpperCase()}
                      </div>
                      <span style={{ fontWeight: 700, fontSize: 13, color: 'var(--ink-1)' }}>{msg.username}</span>
                      <span style={{ fontSize: 11, color: 'var(--ink-4)' }}>
                        {new Date(msg.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        {' · '}
                        {new Date(msg.created_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                      </span>
                    </div>
                    {isAdmin && (
                      <button
                        onClick={() => deleteMessage(msg.id)}
                        style={{ background: 'none', border: 'none', color: 'var(--ink-4)', cursor: 'pointer', padding: '2px 6px', fontSize: 13, lineHeight: 1 }}
                        title="Delete message"
                      >✕</button>
                    )}
                  </div>
                  <div style={{ fontSize: 14, color: 'var(--ink-2)', lineHeight: 1.6, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {msg.content}
                  </div>
                </div>
              ))
            )}
            <div ref={bottomRef} />
          </div>

          {/* Compose */}
          <div className="card" style={{ padding: 16 }}>
            <textarea
              className="form-control"
              rows={3}
              placeholder={`Ask a question or leave a note for ${team.name}...`}
              value={msgContent}
              onChange={e => setMsgContent(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) postMessage(); }}
              style={{ resize: 'vertical', marginBottom: '10px' }}
              maxLength={1000}
            />
            {msgError && <div style={{ color: '#F87171', fontSize: '13px', marginBottom: '8px' }}>{msgError}</div>}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: '11px', color: 'var(--ink-4)' }}>{msgContent.length}/1000 · ⌘↵ to post</span>
              <button
                className="btn btn-primary btn-sm"
                onClick={postMessage}
                disabled={msgLoading || !msgContent.trim()}
              >
                {msgLoading ? 'Posting…' : 'Post Message'}
              </button>
            </div>
          </div>
        </div>
      )}

      </div>{/* /container */}

      {/* Film modal */}
      {watchingGame && (
        <GameVideoModal game={watchingGame} onClose={() => setWatchingGame(null)} />
      )}

      {/* Analysis reading pane */}
      {analysisTarget && (
        <AnalysisPaneModal
          game={analysisTarget.game}
          type={analysisTarget.type}
          onClose={() => setAnalysisTarget(null)}
        />
      )}

      {/* Record override modal */}
      {showRecordEdit && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 2000, padding: '24px',
        }} onClick={e => { if (e.target === e.currentTarget) setShowRecordEdit(false); }}>
          <div style={{
            background: 'var(--surface)', border: '1px solid var(--hairline)', borderRadius: 16,
            padding: 28, width: '100%', maxWidth: 360,
            boxShadow: '0 32px 80px rgba(0,0,0,0.25)',
          }}>
            <div style={{ fontSize: 16, fontWeight: 800, color: 'var(--ink-1)', marginBottom: 6 }}>
              Edit Team Record
            </div>
            <div style={{ fontSize: 13, color: 'var(--ink-3)', marginBottom: 20 }}>
              Override the auto-calculated W/L/T record. The system will use these values instead of computing from game scores.
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12, marginBottom: 20 }}>
              {[
                { label: 'Wins', color: 'var(--win)', val: recordW, set: setRecordW },
                { label: 'Losses', color: 'var(--loss)', val: recordL, set: setRecordL },
                { label: 'Ties', color: 'var(--tie)', val: recordT, set: setRecordT },
              ].map(({ label, color, val, set }) => (
                <div key={label} style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color, textTransform: 'uppercase', letterSpacing: '1px', marginBottom: 8 }}>{label}</div>
                  <input
                    type="number" min={0} value={val}
                    onChange={e => set(e.target.value)}
                    className="form-input"
                    style={{ textAlign: 'center', fontSize: 28, fontWeight: 900, padding: '10px 6px', color }}
                  />
                </div>
              ))}
            </div>
            {(team as any).record?.override && (
              <div style={{ marginBottom: 16, padding: '8px 12px', background: 'rgba(234,179,8,0.08)', border: '1px solid rgba(234,179,8,0.2)', borderRadius: 8, fontSize: 12, color: '#B45309' }}>
                ⚠️ Record is currently manually overridden
              </div>
            )}
            <div style={{ display: 'flex', gap: 8, flexDirection: 'column' }}>
              <button onClick={() => saveRecord(false)} disabled={recordBusy}
                className="btn btn-primary btn-lg" style={{ width: '100%' }}>
                {recordBusy ? 'Saving…' : '✓ Save Record'}
              </button>
              {(team as any).record?.override && (
                <button onClick={() => saveRecord(true)} disabled={recordBusy}
                  className="btn btn-ghost btn-lg" style={{ width: '100%' }}>
                  🔄 Clear Override (use auto-calc)
                </button>
              )}
              <button onClick={() => setShowRecordEdit(false)} disabled={recordBusy}
                className="btn btn-ghost btn-lg" style={{ width: '100%' }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </TeamThemeProvider>
  );
};

export default TeamPage;
